// ============================================================================
// FSA NOTE MODAL  (FSA-2.2 stage 2 — left-menu layout)
// ============================================================================
// Opens for one (engagement, schedule) pair. Hydrates from backend (template
// structure + live TB class breakdown + saved JSONB workspace), then renders
// a left-menu shell with five panes:
//   - Overview               (read-only summary, totals, reconciliation, last-saved)
//   - Ledgers                (TB ledgers classified to this note's classes)
//   - Main Note              (per-class rows, line items, actual/rounded × CY/PY)
//   - Additional Disclosures (collapsible disclosure renderer)
//   - Auditor Notes          (free-form remarks)
//
// Action footer: Save, Submit, Preview (+ standard minimize-to-bar icon in
// header). Status-aware: SUBMITTED / APPROVED notes are read-only with
// alternate actions.
//
// Preview / Submit are wired to placeholder handlers — backend transitions
// + PDF preview ship in FSA-2.2 stage 3.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { fsaClassificationService } from 'api/services/fsa/fsaClassificationService';
import { fsaNotesService } from 'api/services/fsa/fsaNotesService';
import { useFsaNoteHydrate } from 'hooks/api/fsa/useFsaNotes';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import type { ID } from 'api/types';
import type {
  NoteHydrate,
  NoteWorkspaceData,
  NoteClassData,
  NoteLineItem,
  NoteAddedClass,
  NoteIncomingCrossNote,
  NoteInboundPlFlow,
  NoteClassDefaultLineItem,
} from 'api/types/fsa/noteData.types';
import NoteSidebar, { type NotePane } from './notes/NoteSidebar';
import OverviewPane from './notes/panes/OverviewPane';
import LedgersPane from './notes/panes/LedgersPane';
import MainNotePane, { type DisplayMode } from './notes/panes/MainNotePane';
import PolicyEditorPane from './notes/panes/policy/PolicyEditorPane';
import DisclosuresPane from './notes/panes/DisclosuresPane';
import AuditorNotesPane from './notes/panes/AuditorNotesPane';
import FsaNotePreviewModal from './FsaNotePreviewModal';
import { evalClassSubtotal, defaultLineItemLocalId } from './notes/shared/subtotalFormula';
import { recomputeFormulaColumns } from './notes/panes/multi-column/recomputeFormulaColumns';
import type { FormatColumnSpec } from 'api/types/fsa/template.types';

interface Props {
  isOpen: boolean;
  engagementId: ID;
  scheduleId: ID | null;
  /** Used for the modal header — falls back to hydrate response if unset. */
  scheduleHeader?: { code?: string; name?: string };
  onClose: () => void;
  onSaved?: () => void;
  // Minimize-to-bar support
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (scheduleId: ID) => void;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

/**
 * Shallow numeric-map equality — used by setColumnTotals (and similar
 * dict-replacing setters) to skip no-op workspace writes that would
 * otherwise dirty-mark the modal on every render of the multi-col
 * table. Compares key counts, then each numeric value. Treats absent
 * keys and NaN as not equal.
 */
function shallowEqualNumericMap(
  a: Record<string, number>,
  b: Record<string, number>,
): boolean {
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;
  for (const k of aKeys) {
    const av = a[k];
    const bv = b[k];
    if (av !== bv) return false;
  }
  return true;
}

/**
 * Parse the schedule_class.default_line_items JSON string. Returns an
 * empty array for null / empty / unparseable input — the hydrate
 * materialisation step treats "no defaults" identically to "parse
 * failed" so a malformed seed never crashes the modal.
 */
function parseDefaultLineItems(json?: string | null): NoteClassDefaultLineItem[] {
  if (!json || !json.trim()) return [];
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is NoteClassDefaultLineItem =>
        e && typeof e.local_code === 'string' && typeof e.name === 'string',
    );
  } catch {
    return [];
  }
}

const FsaNoteModal: React.FC<Props> = ({
  isOpen,
  engagementId,
  scheduleId,
  scheduleHeader,
  onClose,
  onSaved,
  tabId,
  moduleName,
  onRestoreModal,
  minimizeRef,
}) => {
  const { minimize } = useModalMinimize();
  const { data: hydrate, loading: hydrating, refetch } = useFsaNoteHydrate(
    isOpen ? engagementId : null,
    isOpen ? scheduleId : null,
  );

  // ───── Workspace state (in-memory, before save) ────────────────────────────
  const [workspace, setWorkspace] = useState<NoteWorkspaceData>({
    class_data: {},
    additional_disclosures: {},
    notes_text: null,
    note_remark: null,
  });
  const workspaceRef = useRef(workspace);
  workspaceRef.current = workspace;
  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);
  // FSA — source-driven drift that the merged snapshot would otherwise mask.
  // pl_flow line items (e.g. Note 35 EPS's Profit After Tax, Reserves &
  // Surplus's profit rollover) are READ-ONLY and re-pulled LIVE on every
  // merge, then baked into initialSnapshotRef — so when the source P&L
  // changes after a save, the note recomputes on open but isDirty can't see
  // it. The merge flips this true when an existing pl_flow line's amount no
  // longer matches the freshly-pulled live value, so Save can be offered to
  // persist the recalculated figure.
  const [hasSourceDrift, setHasSourceDrift] = useState(false);
  // Tracks the scheduleId we last reset the active pane to 'overview' for.
  // Reset happens only when the modal opens a DIFFERENT Note — re-hydrates
  // on the same Note (save, refetch, etc.) keep whichever pane the auditor
  // is on. Without this, the post-save UX forces a click back to the
  // Ledgers tab on every save.
  const lastResetForScheduleIdRef = useRef<ID | null>(null);

  // ───── Active pane (left-menu selection) ───────────────────────────────────
  const [activePane, setActivePane] = useState<NotePane>('overview');
  const activePaneRef = useRef(activePane);
  activePaneRef.current = activePane;

  // ───── Display mode (Actual / Rounded) — Note-Modal-level toggle ───────────
  // Lifted out of MainNotePane so the DisclosuresPane below can render in
  // the same mode. Persisted to localStorage so the auditor's choice
  // survives modal close + re-open.
  const DISPLAY_MODE_KEY = 'fsa.note-modal.display-mode';
  const [displayMode, setDisplayModeState] = useState<DisplayMode>(() => {
    if (typeof window === 'undefined') return 'actual';
    try {
      const v = window.localStorage.getItem(DISPLAY_MODE_KEY);
      return v === 'rounded' ? 'rounded' : 'actual';
    } catch { return 'actual'; }
  });
  const setDisplayMode = useCallback((mode: DisplayMode) => {
    setDisplayModeState(mode);
    try { window.localStorage.setItem(DISPLAY_MODE_KEY, mode); } catch { /* noop */ }
  }, []);

  // Clear the "we already reset the pane for this scheduleId" memo when
  // the modal closes — reopening the same Note in a fresh session should
  // land on Overview again. Restore-from-minimize is handled separately:
  // the minimize/onRestore path sets `isRestoreRef` so the hydrate
  // effect bails before touching activePane.
  useEffect(() => {
    if (!isOpen) lastResetForScheduleIdRef.current = null;
  }, [isOpen]);

  // ───── Hydrate workspace from the response when modal opens ────────────────
  useEffect(() => {
    if (!isOpen || !hydrate) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; return; }

    let savedBlob: NoteWorkspaceData = {
      class_data: {},
      additional_disclosures: {},
      notes_text: null,
      note_remark: null,
      _hidden_sections: [],
      added_classes: [],
      class_label_overrides: {},
    };
    if (hydrate.workspace?.data) {
      try {
        savedBlob = JSON.parse(hydrate.workspace.data);
      } catch {
        // ignore parse error; start fresh
      }
    }

    // Start with EVERY saved class_data entry — preserves custom-class
    // amounts (their keys are UUIDs, not template codes, so the loop
    // below would otherwise skip them). Template-class entries get
    // overwritten with live-TB-merged values immediately after.
    //
    // Orphan sweep: drop saved class_data entries whose key matches
    // neither a template class on this schedule NOR an
    // engagement-time custom class declared in added_classes. This
    // cleans up legacy orphans saved before the deleteCustomClass +
    // ClearCustomClassReferences fix landed — without it, a stale
    // saved class_data[<deletedLocalId>] inflates the Note total
    // forever (the Resources/Screenshot 1.png pattern). Note that
    // template-class entries that are no longer in liveClasses
    // (template was edited) ARE kept — they're driven by the schema,
    // not by the auditor, and the right cleanup channel is a template
    // migration, not a silent hydrate drop.
    const validTemplateCodes = new Set(hydrate.liveClasses.map(c => c.classCode));
    const declaredCustomLocalIds = new Set(
      (savedBlob.added_classes ?? []).map(a => a.local_id),
    );
    const cleanedClassData: Record<string, typeof savedBlob.class_data[string]> = {};
    for (const [code, cd] of Object.entries(savedBlob.class_data ?? {})) {
      if (validTemplateCodes.has(code) || declaredCustomLocalIds.has(code)) {
        cleanedClassData[code] = cd;
      }
      // else: orphan from a deleted custom class — drop it. Saving the
      // workspace next will persist the cleaned shape.
    }

    const merged: NoteWorkspaceData = {
      class_data: cleanedClassData,
      additional_disclosures: savedBlob.additional_disclosures ?? {},
      notes_text: savedBlob.notes_text ?? null,
      note_remark: savedBlob.note_remark ?? null,
      _hidden_sections: savedBlob._hidden_sections ?? [],
      added_classes: savedBlob.added_classes ?? [],
      class_label_overrides: savedBlob.class_label_overrides ?? {},
      // Preserve the manually-entered multi-col Previous Year Total row
      // across reopen. Workspace-level, not per-class — same pattern as
      // additional_disclosures / class_label_overrides.
      prior_year_totals: savedBlob.prior_year_totals ?? {},
      // Preserve the persisted multi-col current-year Total row. The
      // first render after hydrate will recompute and (no-op) replace
      // this; on subsequent reopens the saved value carries through
      // so the PDF preview reads identical totals without needing a
      // re-edit.
      column_totals: savedBlob.column_totals ?? {},
    };
    // Default-dimension fallback (template-driven). When a schedule
    // declares a grouping_dimension and a class's meta_data carries
    // `default_dimension_values`, apply those defaults to the class's
    // dimension_values for any key the saved snapshot doesn't already
    // hold. Lets templates ship "this class is Unsecured by default,
    // others Secured by default" without forcing every auditor to tag
    // each class manually on every Note. Saved auditor edits always
    // win — the fallback only fills missing keys.
    const groupingDimensionCode: string | null = (() => {
      if (!hydrate.groupingDimensionJson) return null;
      try {
        const g = JSON.parse(hydrate.groupingDimensionJson) as { code?: string };
        return typeof g.code === 'string' && g.code ? g.code : null;
      } catch { return null; }
    })();
    const parseClassDefaultDimensionValues = (
      metaDataJson: string | null | undefined,
    ): Record<string, string> | null => {
      if (!metaDataJson) return null;
      try {
        const m = JSON.parse(metaDataJson) as { default_dimension_values?: unknown };
        const ddv = m.default_dimension_values;
        if (!ddv || typeof ddv !== 'object') return null;
        const out: Record<string, string> = {};
        for (const [k, v] of Object.entries(ddv as Record<string, unknown>)) {
          if (typeof v === 'string') out[k] = v;
        }
        return Object.keys(out).length > 0 ? out : null;
      } catch { return null; }
    };
    const applyDefaultDimensionValues = (
      saved: Record<string, string> | undefined,
      classDefaults: Record<string, string> | null,
    ): Record<string, string> | undefined => {
      if (!classDefaults) return saved;
      const next: Record<string, string> = { ...(saved ?? {}) };
      let mutated = false;
      for (const [k, v] of Object.entries(classDefaults)) {
        if (groupingDimensionCode && k !== groupingDimensionCode) continue; // only fill the schedule's active dimension
        if (next[k] != null && next[k] !== '') continue; // saved value wins
        next[k] = v;
        mutated = true;
      }
      return mutated ? next : saved;
    };

    // FSA-2.2 saved-as-primary contract: once a Note is saved, its
    // class_data values are the authoritative display source. Subsequent
    // TB changes do NOT silently mutate the saved snapshot. The hydrate
    // pipeline therefore:
    //   • PRESERVES saved class_data[code] when present (post-first-save).
    //   • Only INITIALIZES from live when no saved entry exists yet
    //     (first hydrate / first save / newly classified template class).
    // amount_rounded_auto is always recomputed from amount_actual so the
    // display drift indicator reflects current rounding.
    for (const c of hydrate.liveClasses) {
      const saved = savedBlob.class_data?.[c.classCode];
      const classDefaults = parseClassDefaultDimensionValues(c.metaDataJson);
      if (saved) {
        // Saved entry exists → preserve every field. Refresh only
        // amount_rounded_auto (display-only — auditor's amount_rounded
        // stays untouched).
        const autoRound = roundAuto(saved.amount_actual, hydrate.roundingFactorKey);
        const autoRoundPrior = roundAuto(saved.prior_amount_actual, hydrate.roundingFactorKey);
        merged.class_data[c.classCode] = {
          ...saved,
          amount_rounded_auto:       autoRound,
          prior_amount_rounded_auto: autoRoundPrior,
          dimension_values: applyDefaultDimensionValues(saved.dimension_values, classDefaults),
        };
      } else {
        // No saved entry → initialize from live so the first render has
        // something sensible. The auditor will save (or click "Update
        // from Ledger") to commit the snapshot.
        const autoRound = roundAuto(c.liveClosingActual, hydrate.roundingFactorKey);
        const autoRoundPrior = roundAuto(c.liveOpeningActual, hydrate.roundingFactorKey);
        merged.class_data[c.classCode] = {
          shares:          null,
          share_type:      null,
          face_value:      null,
          paid_status:     null,
          remarks:         null,
          hide_in_print:   false,
          amount_actual:               c.liveClosingActual,
          amount_rounded_auto:         autoRound,
          amount_rounded:              autoRound,
          prior_amount_actual:         c.liveOpeningActual,
          prior_amount_rounded_auto:   autoRoundPrior,
          prior_amount_rounded:        autoRoundPrior,
          ledgers: c.ledgers.map((l, i) => ({
            ledger_id: l.ledgerId,
            ledger_name: l.ledgerName,
            amount_actual: l.closingActual,
            prior_amount_actual: l.openingActual,
            display_order: i + 1,
            remarks: null,
          })),
          line_items: [],
          dimension_values: applyDefaultDimensionValues(undefined, classDefaults),
        };
      }
    }

    // FSA-2.2 (Inventory Changes / Note 28): lazy-materialise the
    // template-default line items. Runs AFTER live-class seeding (so the
    // class_data entry exists) and BEFORE custom-class / cross-note /
    // pl-flow materialisation so subsequent code can rely on the defaults
    // already being present. Idempotent: once a default line item exists
    // (matched by `local_id = default::<local_code>`), the saved row
    // wins — auditor edits to amount / name persist.
    for (const c of hydrate.liveClasses) {
      const defaults = parseDefaultLineItems(c.defaultLineItemsJson);
      if (defaults.length === 0) continue;
      const cd = merged.class_data[c.classCode];
      if (!cd) continue;
      const existingIds = new Set((cd.line_items ?? []).map((li) => li.local_id));
      const toAdd: NoteLineItem[] = [];
      let order = (cd.line_items ?? []).length;
      for (const d of defaults) {
        const localId = defaultLineItemLocalId(d.local_code);
        if (existingIds.has(localId)) continue;
        order += 1;
        toAdd.push({
          local_id: localId,
          name: d.name,
          display_order: order * 10,
          remarks: null,
          allocation_mode: d.allocation_mode,
          ledgers: [],
          manual_amount_actual: null,
          source_ref: null,
          amount_actual: 0,
          amount_rounded_auto: 0,
          amount_rounded: 0,
          prior_amount_actual: 0,
          prior_amount_rounded_auto: 0,
          prior_amount_rounded: 0,
        });
      }
      if (toAdd.length > 0) {
        cd.line_items = [...(cd.line_items ?? []), ...toAdd];
      }
    }

    // FSA-2.2 step 4b — engagement-time custom classes. Same saved-as-
    // primary rule. liveCustomClasses with no ledgers fall through to the
    // saved branch (or stay missing if there's no saved entry — the row
    // was added to added_classes but never received a TB mapping or a
    // manual amount yet; class_data[localId] is preserved as the Main Note
    // pane's own state machine seeded it on creation).
    //
    // Defensive guard: only synthesise a class_data entry for a live custom
    // class that's still DECLARED in added_classes. If the auditor has
    // deleted the custom class from the Note but the TB-side cleanup
    // (Note Modal's deleteCustomClass → ClearCustomClassReferencesAsync)
    // failed or hasn't fired yet, the backend's GetLiveCustomClassesAsync
    // still reports it as "live" via the dangling custom_class_local_id
    // refs. Without this guard, the hydrate would resurrect the deleted
    // class and inflate the Note total with an invisible orphan row
    // (Resources/Screenshot 1.png).
    const declaredCustomIds = new Set(
      (savedBlob.added_classes ?? []).map(a => a.local_id),
    );

    for (const cc of hydrate.liveCustomClasses ?? []) {
      const isDeclared = declaredCustomIds.has(cc.localId);
      const saved = savedBlob.class_data?.[cc.localId];
      if (saved && isDeclared) {
        const autoRound = roundAuto(saved.amount_actual, hydrate.roundingFactorKey);
        const autoRoundPrior = roundAuto(saved.prior_amount_actual, hydrate.roundingFactorKey);
        merged.class_data[cc.localId] = {
          ...saved,
          amount_rounded_auto:       autoRound,
          prior_amount_rounded_auto: autoRoundPrior,
        };
      } else if (cc.ledgerCount > 0 && isDeclared) {
        // No saved entry but TB ledgers are mapped — initialize from live.
        const autoRound = roundAuto(cc.liveClosingActual, hydrate.roundingFactorKey);
        const autoRoundPrior = roundAuto(cc.liveOpeningActual, hydrate.roundingFactorKey);
        merged.class_data[cc.localId] = {
          shares:          null,
          share_type:      null,
          face_value:      null,
          paid_status:     null,
          remarks:         null,
          hide_in_print:   false,
          amount_actual:               cc.liveClosingActual,
          amount_rounded_auto:         autoRound,
          amount_rounded:              autoRound,
          prior_amount_actual:         cc.liveOpeningActual,
          prior_amount_rounded_auto:   autoRoundPrior,
          prior_amount_rounded:        autoRoundPrior,
          ledgers: cc.ledgers.map((l, i) => ({
            ledger_id: l.ledgerId,
            ledger_name: l.ledgerName,
            amount_actual: l.closingActual,
            prior_amount_actual: l.openingActual,
            display_order: i + 1,
            remarks: null,
          })),
          line_items: [],
        };
      }
      // else: orphan (TB refs exist but added_classes doesn't declare it
      // anymore, OR there are no ledgers and no saved entry) — skip
      // synthesis. The TB ledgers' schedule_id remains so they still
      // contribute to the Note's MappedTotal / drift indicators; the
      // user can clean them up via the TB-side classification flow.
    }
    // FSA-2.2 Wave 3 (A) — materialise cross_note line items from the
    // live aggregator. Each incoming ref becomes a uniform NoteLineItem
    // (allocation_mode = 'cross_note', source_ref pointing back) inside
    // the target class's line_items array. No new render component, no
    // synthetic-row component — the existing line-item table handles it.
    //
    // Identity is the deterministic local_id `xn::{srcSched}::{srcClass}::{srcLi|class}`.
    // On re-hydrate this lets us tell "already in workspace" (preserve
    // auditor's saved amount) from "new ref" (insert from live). Refresh
    // -from-source is a per-line replace of the matching local_id.
    const xnLocalId = (r: NoteIncomingCrossNote): string =>
      `xn::${r.sourceScheduleCode}::${r.sourceClassCode}::${r.sourceLineItemLocalId ?? '_class'}`;
    for (const ref of hydrate.incomingCrossNoteAmounts ?? []) {
      const targetCode = ref.targetClassCode;
      if (!targetCode) continue;
      let cd = merged.class_data[targetCode];
      if (!cd) {
        // Target class never received its own data (no TB mappings,
        // never saved) — synthesise a minimal cd so the line item has
        // somewhere to attach.
        cd = {
          shares:          null,
          share_type:      null,
          face_value:      null,
          paid_status:     null,
          remarks:         null,
          hide_in_print:   false,
          amount_actual:               0,
          amount_rounded_auto:         0,
          amount_rounded:              0,
          prior_amount_actual:         0,
          prior_amount_rounded_auto:   0,
          prior_amount_rounded:        0,
          ledgers: [],
          line_items: [],
        };
        merged.class_data[targetCode] = cd;
      }
      const localId = xnLocalId(ref);
      const existing = cd.line_items.find(li => li.local_id === localId);
      if (existing) {
        // Saved snapshot wins — auditor may have edited the amount or
        // the name. Refresh action will re-pull explicitly.
        continue;
      }
      const cyActual = ref.amountCy ?? 0;
      const pyActual = ref.amountPy ?? 0;
      const cyRounded = roundAuto(cyActual, hydrate.roundingFactorKey);
      const pyRounded = roundAuto(pyActual, hydrate.roundingFactorKey);
      // Use the source line item's own name verbatim — the auditor
      // already knows it's a cross-note row from the mode chip; the
      // template-level label ("Current portion of long-term debt") is
      // an audit-narrative for the PDF, not a row label here.
      const liName = ref.sourceLineItemName || ref.label || 'Cross-note';
      cd.line_items = [
        ...cd.line_items,
        {
          local_id: localId,
          name: liName,
          display_order: (cd.line_items.length + 1) * 10,
          allocation_mode: 'cross_note',
          ledgers: [],
          manual_amount_actual: null,
          source_ref: {
            schedule_code: ref.sourceScheduleCode,
            schedule_name: ref.sourceScheduleName,
            class_code: ref.sourceClassCode,
            line_item_local_id: ref.sourceLineItemLocalId,
            line_item_name: ref.sourceLineItemName,
          },
          amount_actual: cyActual,
          amount_rounded_auto: cyRounded,
          amount_rounded: cyRounded,
          prior_amount_actual: pyActual,
          prior_amount_rounded_auto: pyRounded,
          prior_amount_rounded: pyRounded,
        },
      ];
      // Option A rollup — re-derive cd.amount_actual from the line items
      // (own + cross_note). Backend safety-net does the same on save.
      const totalCy = cd.line_items.reduce((s, li) => s + (li.amount_actual ?? 0), 0);
      const totalPy = cd.line_items.reduce((s, li) => s + (li.prior_amount_actual ?? 0), 0);
      const totalCyR = cd.line_items.reduce((s, li) => s + (li.amount_rounded ?? 0), 0);
      const totalPyR = cd.line_items.reduce((s, li) => s + (li.prior_amount_rounded ?? 0), 0);
      cd.amount_actual = totalCy;
      cd.prior_amount_actual = totalPy;
      cd.amount_rounded = totalCyR;
      cd.prior_amount_rounded = totalPyR;
      cd.amount_rounded_auto = roundAuto(totalCy, hydrate.roundingFactorKey);
      cd.prior_amount_rounded_auto = roundAuto(totalPy, hydrate.roundingFactorKey);
    }

    // FSA-2.2 Wave 3 — same materialisation pattern for inbound P&L
    // flows (template-mandated rollover from a Statement line). Local
    // id is deterministic so re-hydrate preserves auditor edits.
    const plLocalId = (f: NoteInboundPlFlow): string =>
      `pl::${f.sourceReportCode}::${f.sourceLineCode}`;
    // Set when a pl_flow override below changes a previously-saved amount —
    // i.e. the source P&L moved since this note was last saved. Drives
    // hasSourceDrift so Save activates (see canSave).
    let plSourceDrift = false;
    for (const flow of hydrate.inboundPlFlows ?? []) {
      // AUDITOR distribution: do NOT materialise — auditor adds per-
      // partner line items themselves. The Note Modal surfaces the
      // source value as a drift/validation hint elsewhere.
      if (flow.distribution !== 'SINGLE') continue;
      if (!flow.sourceIsSaved) continue; // surface as hint only; nothing to materialise
      const targetCode = flow.targetClassCode;
      if (!targetCode) continue;
      let cd = merged.class_data[targetCode];
      if (!cd) {
        cd = {
          shares: null, share_type: null, face_value: null, paid_status: null,
          remarks: null, hide_in_print: false,
          amount_actual: 0, amount_rounded_auto: 0, amount_rounded: 0,
          prior_amount_actual: 0, prior_amount_rounded_auto: 0, prior_amount_rounded: 0,
          ledgers: [], line_items: [],
        };
        merged.class_data[targetCode] = cd;
      }
      const localId = plLocalId(flow);

      // Sign convention bridge — the source Statement (P&L) stores profit
      // as a positive number (Income − Expenses). The target Note (Reserves
      // & Surplus) is a credit-balance schedule, so its display layer
      // flips stored signs to present credits as positive. If we land the
      // PL profit as-is (+X), the credit-balance flip turns it negative on
      // screen — wrong for the "Add: Profit/(Loss) for the year" line.
      // Flip the sign at materialisation when the target schedule is
      // credit-balance so the stored value lines up with the surrounding
      // class-data convention (other liability/equity stored values are
      // negative; display flips them positive).
      const sign = hydrate.isCreditBalance ? -1 : 1;
      const cyActual = (flow.amountCy ?? 0) * sign;
      const pyActual = (flow.amountPy ?? 0) * sign;
      const cyRounded = roundAuto(cyActual, hydrate.roundingFactorKey);
      const pyRounded = roundAuto(pyActual, hydrate.roundingFactorKey);

      // pl_flow line items are READ-ONLY by design (no auditor edit slot
      // in the Note Modal — see the source_ref comment below). So when a
      // saved pl_flow line item already exists for this flow, we still
      // override its amount + sign rather than "saved wins" — the saved
      // value is purely a snapshot of the source at last save, never a
      // manual override, and stale snapshots (e.g. captured before this
      // sign-convention fix) need to refresh.
      const existingIdx = cd.line_items.findIndex(li => li.local_id === localId);
      // A saved pl_flow line whose live value moved = source drift. The
      // override below refreshes it silently; flag it so Save is offered.
      if (existingIdx >= 0) {
        const prevLi = cd.line_items[existingIdx];
        if (Math.abs((prevLi.amount_actual ?? 0) - cyActual) > 0.005
          || Math.abs((prevLi.prior_amount_actual ?? 0) - pyActual) > 0.005) {
          plSourceDrift = true;
        }
      }
      const plLineItem = {
        local_id: localId,
        name: flow.defaultLabel || flow.sourceLineLabel || 'Net Profit',
        display_order: existingIdx >= 0
          ? cd.line_items[existingIdx].display_order
          : (cd.line_items.length + 1) * 10,
        allocation_mode: 'pl_flow' as const,
        ledgers: [],
        manual_amount_actual: null,
        source_ref: {
          // For pl_flow the source_ref naming is reused: schedule_code
          // is repurposed as the source REPORT code, and class_code as
          // the source LINE code. The line_item_local_id slot stays
          // null. The Note Modal's render path treats pl_flow lines
          // the same as cross_note lines (read-only, source chip).
          schedule_code: flow.sourceReportCode,
          schedule_name: flow.sourceReportName,
          class_code: flow.sourceLineCode,
          line_item_local_id: null,
          line_item_name: flow.sourceLineLabel ?? null,
        },
        amount_actual: cyActual,
        amount_rounded_auto: cyRounded,
        amount_rounded: cyRounded,
        prior_amount_actual: pyActual,
        prior_amount_rounded_auto: pyRounded,
        prior_amount_rounded: pyRounded,
      };
      cd.line_items = existingIdx >= 0
        ? cd.line_items.map((li, idx) => (idx === existingIdx ? plLineItem : li))
        : [...cd.line_items, plLineItem];
      const totalCy  = cd.line_items.reduce((s, li) => s + (li.amount_actual ?? 0), 0);
      const totalPy  = cd.line_items.reduce((s, li) => s + (li.prior_amount_actual ?? 0), 0);
      const totalCyR = cd.line_items.reduce((s, li) => s + (li.amount_rounded ?? 0), 0);
      const totalPyR = cd.line_items.reduce((s, li) => s + (li.prior_amount_rounded ?? 0), 0);
      cd.amount_actual = totalCy;
      cd.prior_amount_actual = totalPy;
      cd.amount_rounded = totalCyR;
      cd.prior_amount_rounded = totalPyR;
      cd.amount_rounded_auto = roundAuto(totalCy, hydrate.roundingFactorKey);
      cd.prior_amount_rounded_auto = roundAuto(totalPy, hydrate.roundingFactorKey);
    }

    // ── FSA ICAI NCE — auto-materialise partner rows in partner_lines classes ─
    // Any class flagged meta_data.partner_lines gets one line item per eligible
    // partner (shipped on hydrate.partners), folded in HERE in the merge —
    // before the snapshot — so the rows are baseline (Save stays settled) and
    // behave like any hydrated line item. Idempotent: a saved `partner::<id>`
    // row wins. Two shapes:
    //   • multi-column (Owners' Capital sections) → full column_values dict;
    //     pre-fills Share % (PSR) + Current-Account Share of Profit/(Loss)
    //     = P&L profit (inbound_pl_flow AUDITOR) × PSR.
    //   • simple (Remuneration to Partners) → a plain manual line item; the
    //     auditor enters each partner's amount.
    // Then DERIVED columns are CONSUMED: columns whose column_spec carries a
    // `derived_from` (e.g. Owners' Capital `remuneration`) are overwritten —
    // read-only — from hydrate.partnerDerivedColumns on the appropriation's
    // target section, every hydrate so the source note always wins.
    const partnerList = hydrate.partners ?? [];
    const partnerLinesClasses = (hydrate.liveClasses ?? [])
      .filter(c => {
        try { return (JSON.parse(c.metaDataJson ?? '{}') as { partner_lines?: boolean })?.partner_lines === true; }
        catch { return false; }
      })
      .map(c => c.classCode);
    if (partnerList.length > 0 && partnerLinesClasses.length > 0) {
      const factor = hydrate.roundingFactorKey;
      let pcCols: FormatColumnSpec[] = [];
      try { pcCols = JSON.parse(hydrate.columnSpecJson ?? '[]') as FormatColumnSpec[]; } catch { pcCols = []; }
      const isMultiCol = pcCols.some(c => c.is_total);

      // 1) Materialise MISSING partner rows. Multi-column (Owners' Capital)
      //    rows carry share % (PSR) + zeroed amount columns — appropriations
      //    are placed below by applyAppropriationRouting. Simple notes
      //    (Remuneration) get a plain manual line item.
      for (const classCode of partnerLinesClasses) {
        const cd = merged.class_data[classCode];
        if (!cd) continue;
        const existing = cd.line_items ?? [];
        const existingIds = new Set(existing.map(li => li.local_id));
        const toAdd: NoteLineItem[] = [];
        let order = existing.length;
        for (const p of partnerList) {
          const localId = `partner::${p.id}`;
          if (existingIds.has(localId)) continue; // saved row wins
          order += 1;
          if (isMultiCol) {
            const psr = p.profitSharingRatio ?? 0;
            const cv: Record<string, number> = {};
            for (const col of pcCols) {
              const val = col.code === 'share_pct' ? psr : 0;
              cv[col.code] = val;
              if (col.data_type === 'amount') cv[col.code + '__rounded'] = roundAuto(val, factor);
            }
            toAdd.push({
              local_id: localId, name: p.partnerName, display_order: order * 10,
              remarks: null, allocation_mode: 'manual', ledgers: [],
              manual_amount_actual: null, source_ref: null,
              amount_actual: 0, amount_rounded_auto: 0, amount_rounded: 0,
              prior_amount_actual: 0, prior_amount_rounded_auto: 0, prior_amount_rounded: 0,
              column_values: cv,
            });
          } else {
            toAdd.push({
              local_id: localId, name: p.partnerName, display_order: order * 10,
              remarks: null, allocation_mode: 'manual', ledgers: [],
              manual_amount_actual: null, source_ref: null,
              amount_actual: 0, amount_rounded_auto: 0, amount_rounded: 0,
              prior_amount_actual: 0, prior_amount_rounded_auto: 0, prior_amount_rounded: 0,
            });
          }
        }
        if (toAdd.length > 0) cd.line_items = [...existing, ...toAdd];
      }

      // 2) Place appropriations per the effective routing (Owners' Capital
      //    only) — derived remuneration/interest + computed profit-share land
      //    in their routed section, closings + rollups + column_totals recompute.
      //    Simple notes (Remuneration) have no routing; just roll the class
      //    total from the per-partner line items.
      const apprCtx = buildApprCtx(hydrate);
      if (apprCtx) {
        const effRouting = { ...apprCtx.defaultRouting, ...(savedBlob.appropriation_routing ?? {}) };
        const { classData, columnTotals } = applyAppropriationRouting(merged.class_data, effRouting, apprCtx);
        merged.class_data = classData;
        merged.column_totals = columnTotals;
        merged.appropriation_routing = savedBlob.appropriation_routing ?? {};
      } else {
        for (const classCode of partnerLinesClasses) {
          const cd = merged.class_data[classCode];
          if (!cd) continue;
          const items = cd.line_items ?? [];
          if (items.length === 0) continue;
          const cyA = items.reduce((s, li) => s + (li.amount_actual ?? 0), 0);
          const pyA = items.reduce((s, li) => s + (li.prior_amount_actual ?? 0), 0);
          cd.amount_actual = cyA;
          cd.prior_amount_actual = pyA;
          cd.amount_rounded = roundAuto(cyA, factor);
          cd.prior_amount_rounded = roundAuto(pyA, factor);
          cd.amount_rounded_auto = roundAuto(cyA, factor);
          cd.prior_amount_rounded_auto = roundAuto(pyA, factor);
        }
      }
    }

    setWorkspace(merged);
    initialSnapshotRef.current = JSON.stringify(merged);
    setHasSourceDrift(plSourceDrift);
    // Reset the active pane to 'overview' ONLY on first hydrate of a
    // given (modal-open × scheduleId) — keeps the auditor on their
    // current tab through saves / refetches of the same Note.
    if (hydrate.scheduleId !== lastResetForScheduleIdRef.current) {
      setActivePane('overview');
      lastResetForScheduleIdRef.current = hydrate.scheduleId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, hydrate?.scheduleId, hydrate?.liveClasses.length, hydrate?.liveCustomClasses?.length, hydrate?.workspace?.rowVersion, hydrate?.incomingCrossNoteAmounts, hydrate?.inboundPlFlows, hydrate?.partners, hydrate?.partnerDerivedColumns]);

  // FSA-2.2 (Inventory Changes): map of classCode → subtotal_formula
  // for every live class on this Note that declares one. Looked up by
  // the line-item handlers when computing class totals so the formula
  // (e.g. "OPENING - CLOSING") overrides the default Σ rule. Empty when
  // no class on this Note carries a formula (which is the case for
  // every Schedule III note except N28).
  const subtotalFormulaByCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of hydrate?.liveClasses ?? []) {
      const f = c.subtotalFormula?.trim();
      if (f) m.set(c.classCode, f);
    }
    return m;
    // Re-derive when liveClasses identity changes (hydrate refresh).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrate?.liveClasses]);

  // ───── Dirty tracking ──────────────────────────────────────────────────────
  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(workspace) !== initialSnapshotRef.current;
  }, [workspace]);

  // FSA-2.2 Wave 3 (A) — Save is meaningful in three cases:
  //   1) The auditor has edited the workspace (isDirty).
  //   2) The Note has never been saved (first commit captures the
  //      initial-from-live snapshot — ledger maps, class amounts,
  //      newly-materialised cross_note line items).
  //   3) Σ cd.amount_actual ≠ closing_actual_total — happens when the
  //      hydrate merge just materialised a new cross_note line item
  //      whose source the auditor had saved on the source Note since
  //      this Note was last saved. Option A rollup now includes those
  //      lines in cd.amount_actual, so a direct comparison against the
  //      persisted total surfaces the drift without double-counting.
  //
  // MEMO_ONLY exclusion (Share Capital → Authorised Capital and any
  // future memo-disclosure class): the backend's closing_actual_total
  // excludes MEMO_ONLY classes (their amount is purely disclosure —
  // "Authorised: 700 shares of Rs 100,000 each" rolls into the
  // Authorised Capital memo, NOT the Note's Closing total). The
  // frontend's Σ-comparison must mirror that exclusion or the Save
  // button stays permanently enabled even on a clean Note.
  const memoOnlyClassCodes = useMemo(() => {
    const s = new Set<string>();
    for (const c of hydrate?.liveClasses ?? []) {
      if (c.classRole === 'MEMO_ONLY') s.add(c.classCode);
    }
    return s;
  }, [hydrate?.liveClasses]);

  // MANUAL_DISCLOSURE classes on multi-column Notes (the ageing-matrix
  // input + subtotal + grand-total rows on Trade Payables) are
  // presentation-only — they don't contribute to the Note's total.
  // The Note total comes from the TB-classified leaves (e.g. N7_MSME /
  // N7_OTHERS) which are flagged hide_in_main_note. Filter these out of
  // both the sidebar Total CY / PY display AND canSave's drift check
  // so any stale amount_actual saved on a matrix row (legacy from before
  // the backend's MANUAL_DISCLOSURE skip shipped) doesn't double-count.
  const manualDisclosureClassCodes = useMemo(() => {
    const s = new Set<string>();
    for (const c of hydrate?.liveClasses ?? []) {
      if (c.classRole === 'MANUAL_DISCLOSURE') s.add(c.classCode);
    }
    return s;
  }, [hydrate?.liveClasses]);

  const canSave = useMemo(() => {
    if (isDirty) return true;
    if (!hydrate?.workspace) return true;
    // A read-only pl_flow source (EPS's PAT, Reserves profit rollover)
    // moved since last save — the note recomputed on open but isDirty can't
    // see it (the live value was baked into the snapshot). Offer Save.
    if (hasSourceDrift) return true;
    // FSA-2.4 N2 — live drift counts as something to save. The cells
    // already display the live values (amber-tinted); Save will
    // auto-apply them to the workspace before persisting so the
    // auditor doesn't need a separate "Update from Ledger" click.
    // BOTH periods matter: a PY-only ledger / comparative change (fresh
    // opening TB, dependent-note PY shift) drifts the saved snapshot even
    // when CY is untouched. Gating on CY alone left no way to save the
    // recalculated PY.
    if ((hydrate?.noteLevelDriftCy ?? 0) > 0.005) return true;
    if ((hydrate?.noteLevelDriftPy ?? 0) > 0.005) return true;
    const sumActual = (selector: (cd: typeof workspace.class_data[string]) => number) =>
      Object.entries(workspace.class_data).reduce((s, [code, cd]) => {
        if (memoOnlyClassCodes.has(code)) return s;
        if (manualDisclosureClassCodes.has(code)) return s;
        return s + selector(cd);
      }, 0);
    if (Math.abs(sumActual(cd => cd.amount_actual ?? 0) - hydrate.workspace.closingActualTotal) > 0.005) return true;
    return Math.abs(sumActual(cd => cd.prior_amount_actual ?? 0) - hydrate.workspace.openingActualTotal) > 0.005;
  }, [isDirty, hasSourceDrift, hydrate?.workspace, hydrate?.noteLevelDriftCy, hydrate?.noteLevelDriftPy, workspace.class_data, memoOnlyClassCodes, manualDisclosureClassCodes]);

  // ───── Field setters ───────────────────────────────────────────────────────
  const setClassField = useCallback(<K extends keyof NoteClassData>(
    classCode: string,
    field: K,
    value: NoteClassData[K],
  ) => {
    setWorkspace(prev => {
      const cd = prev.class_data[classCode];
      if (!cd) return prev;
      return {
        ...prev,
        class_data: { ...prev.class_data, [classCode]: { ...cd, [field]: value } },
      };
    });
  }, []);

  const setNotesText = useCallback((text: string) => {
    setWorkspace(prev => ({ ...prev, notes_text: text }));
  }, []);

  /**
   * Deliverable Note-level free text (separate from PRIVATE reviewer
   * notes_text). Surfaces as a textarea below the class total on the
   * Main Note pane and renders into the printed PDF.
   */
  const setNoteRemark = useCallback((text: string) => {
    setWorkspace(prev => ({ ...prev, note_remark: text }));
  }, []);

  /**
   * Multi-column note Previous Year Total — atomic write of one column's
   * value into `workspace.prior_year_totals[columnCode]`. Same key as the
   * column_spec code; the multi-col footer reads
   * `prior_year_totals[code]` for actual mode and
   * `prior_year_totals[code + '__rounded']` for rounded mode.
   *
   * Passing `null` clears the entry — the cell falls back to blank.
   */
  const setPriorYearTotal = useCallback((columnCode: string, value: number | null) => {
    setWorkspace(prev => {
      const cur = prev.prior_year_totals ?? {};
      const next = { ...cur };
      if (value == null) delete next[columnCode];
      else next[columnCode] = value;
      return { ...prev, prior_year_totals: next };
    });
  }, []);

  /**
   * Multi-column note current-year Total row — UI-computed, persisted
   * verbatim. Replaces the whole <c>column_totals</c> map each call;
   * the source useMemo in MultiColumnClassTable hands over the fully
   * formed dict (both actuals and __rounded companions). Skipped when
   * the incoming dict is shallow-equal to the existing one, so a
   * mid-edit re-render doesn't dirty the workspace pointlessly.
   *
   * Saved with the workspace; PDF hydrator reads it as-is and the
   * renderer prints it without any server-side sum.
   */
  const setColumnTotals = useCallback((totals: Record<string, number>) => {
    setWorkspace(prev => {
      const cur = prev.column_totals ?? null;
      if (cur && shallowEqualNumericMap(cur, totals)) return prev;
      return { ...prev, column_totals: totals };
    });
  }, []);

  // FSA ICAI NCE — live re-route of an appropriation (Owners' Capital).
  // Records the engagement override in workspace.appropriation_routing and
  // re-places every appropriation's per-partner value into its routed section
  // (recomputing closings + rollups + column_totals) using the SAME function
  // the hydrate merge uses, so the change takes effect immediately.
  const setAppropriationRouting = useCallback((col: string, section: string) => {
    if (!hydrate) return;
    setWorkspace(prev => {
      const ctx = buildApprCtx(hydrate);
      if (!ctx) return prev;
      const nextRouting = { ...(prev.appropriation_routing ?? {}), [col]: section };
      const effRouting = { ...ctx.defaultRouting, ...nextRouting };
      const { classData, columnTotals } = applyAppropriationRouting(prev.class_data, effRouting, ctx);
      return { ...prev, class_data: classData, column_totals: columnTotals, appropriation_routing: nextRouting };
    });
  }, [hydrate]);

  /**
   * Toggles or sets the per-class hide_in_print flag. Skipped from the
   * printed PDF (and visually muted in the Main Note pane) when TRUE.
   * Saved totals are NOT recomputed — hiding is a print-layout choice,
   * not an exclusion. A drift validation surfaces if a hidden class
   * carries non-zero amounts so the approver sees the trade-off.
   */
  const setClassHideInPrint = useCallback((classCode: string, hide: boolean) => {
    setWorkspace(prev => {
      const cd = prev.class_data[classCode];
      if (!cd) return prev;
      return {
        ...prev,
        class_data: {
          ...prev.class_data,
          [classCode]: { ...cd, hide_in_print: hide },
        },
      };
    });
  }, []);

  /**
   * Toggles a disclosure section's presence in workspace._hidden_sections.
   * PDF renderer skips any spec whose code is here. UI shows the
   * disclosure muted in DisclosuresPane so the auditor still sees its
   * data while reviewing.
   */
  const toggleDisclosureHidden = useCallback((code: string) => {
    setWorkspace(prev => {
      const current = prev._hidden_sections ?? [];
      const next = current.includes(code)
        ? current.filter(c => c !== code)
        : [...current, code];
      return { ...prev, _hidden_sections: next };
    });
  }, []);

  /**
   * One-click helper — flags every class whose actual AND rounded
   * amounts are zero (both CY and PY) as hidden in PDF. Idempotent
   * (re-running adds no new flags). Reverse by toggling individual
   * classes off via the class action menu.
   */
  const autoHideEmptyClasses = useCallback(() => {
    setWorkspace(prev => {
      const next = { ...prev, class_data: { ...prev.class_data } };
      let changed = false;
      for (const [code, cd] of Object.entries(prev.class_data)) {
        if (cd.hide_in_print) continue;
        const isEmpty =
          (cd.amount_actual ?? 0) === 0 &&
          (cd.amount_rounded ?? 0) === 0 &&
          (cd.prior_amount_actual ?? 0) === 0 &&
          (cd.prior_amount_rounded ?? 0) === 0;
        if (isEmpty) {
          next.class_data[code] = { ...cd, hide_in_print: true };
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, []);

  /**
   * Add an engagement-time custom class at the SAME LEVEL as template
   * classes (sibling, not nested). Caller passes a pre-computed
   * display_order (decimal allowed) so the new class lands at the
   * right position in the merged top-level list. Also seeds a blank
   * class_data[local_id] entry so the new bucket has somewhere to hold
   * amount inputs immediately. Save pipeline treats it as
   * MANUAL_DISCLOSURE.
   */
  const addCustomClass = useCallback((name: string, displayOrder: number) => {
    setWorkspace(prev => {
      const localId = crypto.randomUUID();
      const existing = prev.added_classes ?? [];
      const newAdded: NoteAddedClass = {
        local_id: localId,
        name: name.trim(),
        display_order: displayOrder,
        remarks: null,
      };
      return {
        ...prev,
        added_classes: [...existing, newAdded],
        class_data: {
          ...prev.class_data,
          [localId]: {
            shares: null,
            share_type: null,
            face_value: null,
            paid_status: null,
            remarks: null,
            hide_in_print: false,
            amount_actual: 0,
            amount_rounded_auto: 0,
            amount_rounded: 0,
            prior_amount_actual: 0,
            prior_amount_rounded_auto: 0,
            prior_amount_rounded: 0,
            ledgers: [],
            line_items: [],
          },
        },
      };
    });
  }, []);

  /** Rename an existing custom class. Identified by local_id. */
  const renameCustomClass = useCallback((localId: string, name: string) => {
    setWorkspace(prev => ({
      ...prev,
      added_classes: (prev.added_classes ?? []).map(a =>
        a.local_id === localId ? { ...a, name: name.trim() } : a,
      ),
    }));
  }, []);

  /**
   * Per-engagement rename override for a TEMPLATE class label.
   * Auditor's typed label replaces the template label everywhere it
   * renders (Note Modal + PDF), but the seed label stays canonical in
   * the audit trail. Passing null / empty string CLEARS the override.
   */
  const setClassLabelOverride = useCallback((classCode: string, label: string | null) => {
    setWorkspace(prev => {
      const next = { ...(prev.class_label_overrides ?? {}) };
      if (label === null || !label.trim()) {
        delete next[classCode];
      } else {
        next[classCode] = label.trim();
      }
      return { ...prev, class_label_overrides: next };
    });
  }, []);

  /**
   * Delete a custom class — three steps, in order:
   *
   *   1. Frontend: remove the declaration from <c>added_classes</c> AND
   *      the entry from <c>class_data</c>. Both must go together — leaving
   *      <c>class_data[localId]</c> behind makes the deleted class's
   *      amount keep contributing to the Note total even though no row
   *      renders for it.
   *
   *   2. Backend (when ledgers were mapped): clear every TB entry whose
   *      <c>custom_class_local_id</c> points at this localId. Without
   *      this sweep the next hydrate's <c>GetLiveCustomClassesAsync</c>
   *      query — which derives "live custom classes" from TB references
   *      — would resurrect the deleted class. The hydrate merge would
   *      then re-synthesise <c>class_data[localId]</c> from the live TB
   *      sum, and the orphan total reappears (the bug in
   *      Resources/Screenshot 1.png).
   *
   *      Affected ledgers keep their <c>schedule_id</c> so they remain
   *      attached to this Note as "schedule-only / pending class split"
   *      — the auditor can remap them to another class later, or leave
   *      them as schedule-only.
   *
   *   3. Toast — tell the auditor how many ledger refs were cleaned.
   */
  const deleteCustomClass = useCallback((localId: string) => {
    setWorkspace(prev => {
      const { [localId]: _gone, ...rest } = prev.class_data;
      return {
        ...prev,
        added_classes: (prev.added_classes ?? []).filter(a => a.local_id !== localId),
        class_data: rest,
      };
    });

    // Fire-and-forget the TB-side cleanup. Guard on consolidated TB id
    // because the cleanup endpoint targets the engagement's CONSOLIDATED
    // TB row; without it (engagement just opened, no TB uploaded yet)
    // there's nothing to clear, and the user can drop the class without
    // network noise.
    const tbId = hydrate?.consolidatedTrialBalanceId;
    if (!tbId) return;

    (async () => {
      try {
        const r = await fsaClassificationService.clearCustomClassReferences(tbId, localId);
        const n = r.data ?? 0;
        if (n > 0) {
          notificationService.toast.info(
            `Custom class deleted. ${n} ledger reference${n === 1 ? '' : 's'} cleared.`,
          );
        }
      } catch (err: any) {
        notificationService.toast.error(
          'Custom class deleted from the Note, but clearing dependent ledger references failed — '
          + 'reopening the Note may resurrect it. '
          + (err?.response?.data?.message ?? err?.message ?? ''),
          6000,
        );
      }
    })();
  }, [hydrate?.consolidatedTrialBalanceId]);

  /**
   * One-click helper — adds every disclosure section whose saved value
   * is missing / empty to _hidden_sections so the PDF skips them.
   * Idempotent. specCodes pre-filters to known sections so unknown
   * keys don't pollute the list.
   */
  const autoHideEmptyDisclosures = useCallback((specCodes: string[]) => {
    setWorkspace(prev => {
      const current = new Set(prev._hidden_sections ?? []);
      let changed = false;
      for (const code of specCodes) {
        if (current.has(code)) continue;
        const value = prev.additional_disclosures[code];
        const isEmpty = value == null
          || (typeof value === 'string' && value.trim() === '')
          || (Array.isArray(value) && value.length === 0)
          || (typeof value === 'object' && Object.keys(value).length === 0);
        if (isEmpty) {
          current.add(code);
          changed = true;
        }
      }
      return changed ? { ...prev, _hidden_sections: Array.from(current) } : prev;
    });
  }, []);

  const setDisclosure = useCallback((code: string, value: unknown) => {
    setWorkspace(prev => ({
      ...prev,
      additional_disclosures: { ...prev.additional_disclosures, [code]: value },
    }));
  }, []);

  /**
   * Update a MANUAL_DISCLOSURE class's actual amount AND recompute its
   * rounded companions in the same patch. Generic setClassField only
   * writes the named field, which leaves rounded_auto / rounded stale (0
   * for a freshly-entered manual class). Auditor types the actual, this
   * setter mirrors it into the rounded surface using the engagement's
   * factor — same auto-round contract the backend uses on save.
   */
  // Per-class rounding-exempt lookup — classes with
  // meta_data.display_options.rounding_exempt = true (Note 35 share
  // counts + per-share values) skip the engagement rounding factor:
  // their `amount_rounded` mirror equals `amount_actual`. Without this,
  // typing 400 in WAvg Basic stored amount_rounded = 0.3 under
  // THOUSANDS, breaking the EPS DIVIDE rule downstream
  // (profit_rounded / 0.3 → 14585 instead of profit_rounded / 400 → 10.94).
  const parseRoundingExempt = useCallback((metaDataJson?: string | null): boolean => {
    if (!metaDataJson || metaDataJson === '{}') return false;
    try {
      const r = JSON.parse(metaDataJson) as { display_options?: { rounding_exempt?: boolean } };
      return r.display_options?.rounding_exempt === true;
    } catch { return false; }
  }, []);

  const isRoundingExemptClass = useCallback((classCode: string): boolean => {
    const lc = (hydrate?.liveClasses ?? []).find(c => c.classCode === classCode);
    return parseRoundingExempt(lc?.metaDataJson);
  }, [hydrate?.liveClasses, parseRoundingExempt]);

  const setClassActual = useCallback((
    classCode: string,
    field: 'amount_actual' | 'prior_amount_actual',
    value: number,
  ) => {
    setWorkspace(prev => {
      const cd = prev.class_data[classCode];
      if (!cd) return prev;
      const factor = hydrate?.roundingFactorKey;
      const exempt = isRoundingExemptClass(classCode);
      // For rounding-exempt classes (share counts, per-share values),
      // amount_rounded = amount_actual. Otherwise apply the engagement
      // factor — same contract the backend uses on save.
      const auto = exempt ? value : roundAuto(value, factor);
      const isPrior = field === 'prior_amount_actual';
      const nextCd: NoteClassData = isPrior
        ? {
            ...cd,
            prior_amount_actual: value,
            prior_amount_rounded_auto: auto,
            prior_amount_rounded: auto,
          }
        : {
            ...cd,
            amount_actual: value,
            amount_rounded_auto: auto,
            amount_rounded: auto,
          };
      const nextClassData = { ...prev.class_data, [classCode]: nextCd };

      // Live re-evaluation of is_computed classes — mirrors the backend's
      // EvaluateAndInjectComputedClassesAsync so SUM and DIVIDE results
      // update as the auditor types into their operands. STATEMENT_VALUE
      // rules are not re-evaluated here (those come from saved Statement
      // workspace and are hydrated server-side).
      const computedDefs = (hydrate?.liveClasses ?? [])
        .filter(c => c.isComputed && c.computationRuleJson && c.computationRuleJson !== '{}')
        .map(c => {
          let rule: { type?: string; operands?: string[]; numerator_class?: string; denominator_class?: string } | null = null;
          try { rule = JSON.parse(c.computationRuleJson!); } catch { /* skip */ }
          return { code: c.classCode, rule };
        })
        .filter(x => x.rule != null) as Array<{ code: string; rule: NonNullable<{ type?: string; operands?: string[]; numerator_class?: string; denominator_class?: string }> }>;

      if (computedDefs.length === 0) {
        return { ...prev, class_data: nextClassData };
      }

      const get = (code: string, f: 'amount_actual' | 'prior_amount_actual' | 'amount_rounded' | 'prior_amount_rounded'): number =>
        ((nextClassData[code] as any)?.[f] ?? 0);
      const safeDiv = (n: number, d: number): number => (d === 0 ? 0 : Math.round((n / d) * 100) / 100);

      // Pass A — SUM
      for (const { code, rule } of computedDefs) {
        if ((rule.type ?? '').toUpperCase() !== 'SUM') continue;
        const ops = rule.operands ?? [];
        if (ops.length === 0) continue;
        let cy = 0, py = 0, cyR = 0, pyR = 0;
        for (const o of ops) {
          if (!o) continue;
          cy  += get(o, 'amount_actual');
          py  += get(o, 'prior_amount_actual');
          cyR += get(o, 'amount_rounded');
          pyR += get(o, 'prior_amount_rounded');
        }
        const prevCd = nextClassData[code] ?? ({ line_items: [], ledgers: [] } as any);
        const exempt = isRoundingExemptClass(code);
        nextClassData[code] = {
          ...prevCd,
          amount_actual: Math.round(cy * 100) / 100,
          prior_amount_actual: Math.round(py * 100) / 100,
          amount_rounded: Math.round(cyR * 100) / 100,
          prior_amount_rounded: Math.round(pyR * 100) / 100,
          // Rounding-exempt computed classes never apply the engagement
          // factor — auto mirrors actual (matches the operand convention).
          amount_rounded_auto: exempt ? Math.round(cy * 100) / 100 : roundAuto(cy, factor),
          prior_amount_rounded_auto: exempt ? Math.round(py * 100) / 100 : roundAuto(py, factor),
        };
      }

      // Pass B — DIVIDE (consumes Pass A results)
      for (const { code, rule } of computedDefs) {
        if ((rule.type ?? '').toUpperCase() !== 'DIVIDE') continue;
        const n = rule.numerator_class ?? '';
        const d = rule.denominator_class ?? '';
        if (!n || !d) continue;
        const cy  = safeDiv(get(n, 'amount_actual'),        get(d, 'amount_actual'));
        const py  = safeDiv(get(n, 'prior_amount_actual'),  get(d, 'prior_amount_actual'));
        const cyR = safeDiv(get(n, 'amount_rounded'),       get(d, 'amount_rounded'));
        const pyR = safeDiv(get(n, 'prior_amount_rounded'), get(d, 'prior_amount_rounded'));
        const prevCd = nextClassData[code] ?? ({ line_items: [], ledgers: [] } as any);
        const exempt = isRoundingExemptClass(code);
        nextClassData[code] = {
          ...prevCd,
          amount_actual: cy,
          prior_amount_actual: py,
          amount_rounded: cyR,
          prior_amount_rounded: pyR,
          // Same rule as Pass A — EPS classes are rounding-exempt so
          // amount_rounded_auto mirrors the rounded division result
          // (4375.52 / 400 = 10.94), not roundAuto(cy = 10938.79, THOUSANDS).
          amount_rounded_auto: exempt ? cyR : roundAuto(cy, factor),
          prior_amount_rounded_auto: exempt ? pyR : roundAuto(py, factor),
        };
      }

      return { ...prev, class_data: nextClassData };
    });
  }, [hydrate?.roundingFactorKey, hydrate?.liveClasses, isRoundingExemptClass]);

  /**
   * Replace the full line_items array on a class.
   *
   * FSA-2.2 Option A — class amount is the rollup of its line items.
   * When line items are present, cd.amount_actual / amount_rounded /
   * prior_amount_actual / prior_amount_rounded are recomputed from the
   * line item sums; auto-rounded fields are re-derived from the new
   * actual rollup. Without this, the screen's per-group subtotals
   * (which are Σ li.amount_actual) and the grand Note total (which is
   * Σ cd.amount_actual) drift apart whenever line-item cross-notes
   * are added — class amount stays at the gross from the last
   * "Update from Ledger" refresh while line items track LT-net.
   *
   * When the array is empty (auditor cleared all line items), we leave
   * cd.amount_actual untouched — the class falls back to whatever the
   * ledger / manual entry produced for it.
   */
  const setLineItems = useCallback((classCode: string, lineItems: NoteLineItem[]) => {
    setWorkspace(prev => {
      const cd = prev.class_data[classCode];
      if (!cd) return prev;
      const factor = hydrate?.roundingFactorKey;
      let nextCd: typeof cd = { ...cd, line_items: lineItems };
      if (lineItems.length > 0) {
        // FSA-2.2 (Inventory Changes): honour template-declared subtotal
        // formula (e.g. "OPENING - CLOSING") when present; else default Σ.
        const formula = subtotalFormulaByCode.get(classCode);
        const cyActual  = evalClassSubtotal(lineItems, 'amount_actual',         formula);
        const pyActual  = evalClassSubtotal(lineItems, 'prior_amount_actual',   formula);
        const cyRounded = evalClassSubtotal(lineItems, 'amount_rounded',        formula);
        const pyRounded = evalClassSubtotal(lineItems, 'prior_amount_rounded',  formula);
        nextCd = {
          ...nextCd,
          amount_actual: cyActual,
          prior_amount_actual: pyActual,
          amount_rounded: cyRounded,
          prior_amount_rounded: pyRounded,
          amount_rounded_auto: roundAuto(cyActual, factor),
          prior_amount_rounded_auto: roundAuto(pyActual, factor),
        };
      }
      return {
        ...prev,
        class_data: {
          ...prev.class_data,
          [classCode]: nextCd,
        },
      };
    });
  }, [hydrate?.roundingFactorKey, subtotalFormulaByCode]);

  /**
   * Update a single line_item's rounded field in-place. Used by the
   * inline rounded-amount editor in the line items table. Underlying
   * actual amounts are derived from TB (ledger mode) or the manual entry
   * (manual mode) and stay untouched here.
   */
  const setLineItemRounded = useCallback((
    classCode: string,
    localId: string,
    field: 'amount_rounded' | 'prior_amount_rounded',
    value: number,
  ) => {
    setWorkspace(prev => {
      const cd = prev.class_data[classCode];
      if (!cd) return prev;
      const nextItems = cd.line_items.map(li =>
        li.local_id === localId ? { ...li, [field]: value } : li,
      );
      return {
        ...prev,
        class_data: {
          ...prev.class_data,
          [classCode]: { ...cd, line_items: nextItems },
        },
      };
    });
  }, []);

  const resetRoundingFromActuals = useCallback(() => {
    setWorkspace(prev => {
      const next: NoteWorkspaceData = { ...prev, class_data: { ...prev.class_data } };
      for (const [code, cd] of Object.entries(prev.class_data)) {
        const lineItems = cd.line_items ?? [];
        // FSA-2.4 — Per the "displayed total = sum of displayed lines"
        // principle: when a class has line items, reset each line's
        // rounded to its own auto-rounded FIRST, then derive the class
        // rounded as the sum (honouring any template subtotal formula).
        // Previously, class rounded was reset to cd.amount_rounded_auto
        // (a standalone roundAuto of the class actual) — which can
        // differ from Σ(line rounded) on multi-class notes like
        // Long-term Borrowings, producing a visible "rounded total
        // does not reconcile with line items" inconsistency.
        if (lineItems.length === 0) {
          next.class_data[code] = {
            ...cd,
            amount_rounded: cd.amount_rounded_auto,
            prior_amount_rounded: cd.prior_amount_rounded_auto,
          };
          continue;
        }
        const resetLineItems = lineItems.map(li => ({
          ...li,
          amount_rounded: li.amount_rounded_auto,
          prior_amount_rounded: li.prior_amount_rounded_auto,
        }));
        const formula = subtotalFormulaByCode.get(code);
        const classRoundedCy = evalClassSubtotal(resetLineItems, 'amount_rounded',       formula);
        const classRoundedPy = evalClassSubtotal(resetLineItems, 'prior_amount_rounded', formula);
        next.class_data[code] = {
          ...cd,
          line_items: resetLineItems,
          amount_rounded: classRoundedCy,
          prior_amount_rounded: classRoundedPy,
        };
      }
      return next;
    });
  }, [subtotalFormulaByCode]);

  /**
   * FSA-2.2 saved-as-primary: explicit "Update from Ledger" action.
   *
   * Saved values are authoritative until the auditor decides to re-pull
   * from the live trial balance. This method writes the current live
   * snapshot into class_data[code] — actual amounts, ledger list, and
   * fresh auto-rounded values. amount_rounded is also reset to the new
   * auto-rounded value (the previous rounded was tied to the previous
   * actual; keeping it would be incoherent). The auditor can manually
   * tweak amount_rounded afterwards.
   *
   * Per-ledger remarks the auditor entered are preserved by matching on
   * ledger_id across the old and new ledger lists.
   *
   * Looking up the live data: template classes via liveClasses[classCode],
   * custom classes via liveCustomClasses[localId] (classCode === localId
   * for custom rows by construction).
   */

  /**
   * FSA-2.2 Wave 1 (#1): set a class-level dimension_values entry. Used
   * by the Main Note pane's class-direct action menu — auditor tags a
   * class without line items (e.g. Bonds → Secured). Pass value=null to
   * clear. Empty objects are normalised to undefined so the workspace
   * JSON stays clean.
   */
  const setClassDimensionValue = useCallback(
    (classCode: string, dimCode: string, value: string | null) => {
      setWorkspace(prev => {
        const cd = prev.class_data[classCode];
        if (!cd) return prev;
        const nextDimValues: Record<string, string> = { ...(cd.dimension_values ?? {}) };
        if (value) {
          nextDimValues[dimCode] = value;
        } else {
          delete nextDimValues[dimCode];
        }
        return {
          ...prev,
          class_data: {
            ...prev.class_data,
            [classCode]: {
              ...cd,
              dimension_values: Object.keys(nextDimValues).length > 0 ? nextDimValues : undefined,
            },
          },
        };
      });
    },
    [],
  );

  const applyLedgerSnapshot = useCallback((classCode: string) => {
    if (!hydrate) return;
    const liveTemplate = hydrate.liveClasses.find(c => c.classCode === classCode);
    const liveCustom = hydrate.liveCustomClasses?.find(cc => cc.localId === classCode);
    const liveClosing = liveTemplate?.liveClosingActual ?? liveCustom?.liveClosingActual ?? 0;
    const liveOpening = liveTemplate?.liveOpeningActual ?? liveCustom?.liveOpeningActual ?? 0;
    const liveLedgers = liveTemplate?.ledgers ?? liveCustom?.ledgers ?? [];

    setWorkspace(prev => {
      const cd = prev.class_data[classCode];
      if (!cd) return prev;

      // FSA-2.2 Wave 2: preserve the auditor's cross-note amount across
      // the refresh. The displayed long-term value = live gross − the
      // auditor's cross-note. amount_actual is the long-term; cross-note
      // stays as-saved. Update from Ledger is explicitly a refresh of
      // the long-term portion (per saved-as-primary contract).
      const classCrossNoteCy = cd.cross_note_amount ?? 0;
      const classCrossNotePy = cd.prior_cross_note_amount ?? 0;
      const classLongTermCy = liveClosing - classCrossNoteCy;
      const classLongTermPy = liveOpening - classCrossNotePy;
      const autoRound = roundAuto(classLongTermCy, hydrate.roundingFactorKey);
      const autoRoundPrior = roundAuto(classLongTermPy, hydrate.roundingFactorKey);

      // Carry the auditor's per-ledger remarks across the refresh.
      const remarksByLedgerId = new Map<string, string | null | undefined>();
      for (const saved of cd.ledgers) {
        if (saved.ledger_id != null) remarksByLedgerId.set(String(saved.ledger_id), saved.remarks);
      }

      // FSA-2.2 multi-ledger line items: refresh ledger-mode line item
      // amounts + the per-ledger snapshot under each line. Manual line
      // items pass through unchanged.
      //
      // Orphan rule: a ledger reference whose ledger_id no longer appears
      // on the live class is REMOVED from the line item entirely (the
      // ledger has been reclassified to another class or unmapped). Its
      // contribution drops to zero on refresh. Sum + per-ledger snapshot
      // rebuild from the surviving references only.
      //
      // Wave 2 add-on: each line item also preserves its own cross-note
      // amount across the refresh (long-term = live sum − cross-note).
      //
      // FSA-2.2 multi-col (PPE / Intangibles): also sync the refreshed
      // CY/PY into the line item's column_values[is_total / is_prior_year]
      // (+ their __rounded companions). Without this, the LINE ITEM row
      // in the multi-col table keeps rendering the stale Net Block while
      // the class row (which reads from cd.column_values, written below)
      // shows the refreshed amount. Parse the column_spec ONCE here so
      // we don't repeat the JSON.parse inside the per-line-item loop.
      let lineItemTotalCode: string | null = null;
      let lineItemPriorCode: string | null = null;
      if (hydrate.columnSpecJson) {
        try {
          const parsed = JSON.parse(hydrate.columnSpecJson) as Array<{
            code?: string; is_total?: boolean; is_prior_year?: boolean;
          }>;
          lineItemTotalCode = parsed.find(c => c.is_total)?.code ?? null;
          lineItemPriorCode = parsed.find(c => c.is_prior_year)?.code ?? null;
        } catch { /* malformed spec — fall through; column_values left alone */ }
      }

      const liveLedgerById = new Map(liveLedgers.map(l => [String(l.ledgerId), l]));
      const refreshedLineItems = cd.line_items.map(li => {
        if (li.allocation_mode !== 'ledgers') return li;
        let cySum = 0, pySum = 0;
        let displayOrder = 0;
        const nextLedgers = li.ledgers.flatMap(lref => {
          const live = lref.ledger_id != null
            ? liveLedgerById.get(String(lref.ledger_id))
            : undefined;
          if (!live) return [];
          cySum += live.closingActual;
          pySum += live.openingActual;
          return [{
            ...lref,
            ledger_name: live.ledgerName,
            amount_actual: live.closingActual,
            prior_amount_actual: live.openingActual,
            display_order: ++displayOrder,
          }];
        });
        const liCrossNoteCy = li.cross_note_amount ?? 0;
        const liCrossNotePy = li.prior_cross_note_amount ?? 0;
        const liLongTermCy = cySum - liCrossNoteCy;
        const liLongTermPy = pySum - liCrossNotePy;
        const cyAuto = roundAuto(liLongTermCy, hydrate.roundingFactorKey);
        const pyAuto = roundAuto(liLongTermPy, hydrate.roundingFactorKey);

        // Multi-col: write the refreshed CY/PY into the line item's
        // column_values for the is_total / is_prior_year columns + their
        // __rounded companions. Other column_values entries (movement
        // detail the auditor typed in) pass through unchanged.
        let liNextColumnValues = li.column_values;
        if (lineItemTotalCode || lineItemPriorCode) {
          liNextColumnValues = { ...(li.column_values ?? {}) };
          if (lineItemTotalCode) {
            liNextColumnValues[lineItemTotalCode] = liLongTermCy;
            liNextColumnValues[lineItemTotalCode + '__rounded'] = cyAuto;
          }
          if (lineItemPriorCode) {
            liNextColumnValues[lineItemPriorCode] = liLongTermPy;
            liNextColumnValues[lineItemPriorCode + '__rounded'] = pyAuto;
          }
        }

        return {
          ...li,
          ledgers: nextLedgers,
          amount_actual: liLongTermCy,
          amount_rounded_auto: cyAuto,
          amount_rounded: cyAuto,
          prior_amount_actual: liLongTermPy,
          prior_amount_rounded_auto: pyAuto,
          prior_amount_rounded: pyAuto,
          ...(liNextColumnValues ? { column_values: liNextColumnValues } : {}),
        };
      });

      // Option A — class amount = line-item rollup when present; falls
      // back to (live − class cross-note) only for classes that don't
      // have any line items.
      //
      // FSA-2.2 (Inventory Changes / Note 28 fix): honour the template's
      // class-level `subtotal_formula` (e.g. "OPENING - CLOSING") instead
      // of a plain Σ. Without this branch, Update from Ledger silently
      // replaces the formula-derived class amount with Σ(line items),
      // which flips the sign on classes like "Changes in WIP" where
      // closing > opening. recalculateAllTotals already uses the formula
      // via evalClassSubtotal — this path was the only one bypassing it.
      let classAmountCy = classLongTermCy;
      let classAmountPy = classLongTermPy;
      let classAmountRoundedCy = autoRound;
      let classAmountRoundedPy = autoRoundPrior;
      let classAmountAutoCy = autoRound;
      let classAmountAutoPy = autoRoundPrior;
      if (refreshedLineItems.length > 0) {
        const formula = subtotalFormulaByCode.get(classCode);
        classAmountCy        = evalClassSubtotal(refreshedLineItems, 'amount_actual',        formula);
        classAmountPy        = evalClassSubtotal(refreshedLineItems, 'prior_amount_actual',  formula);
        classAmountRoundedCy = evalClassSubtotal(refreshedLineItems, 'amount_rounded',       formula);
        classAmountRoundedPy = evalClassSubtotal(refreshedLineItems, 'prior_amount_rounded', formula);
        classAmountAutoCy    = roundAuto(classAmountCy, hydrate.roundingFactorKey);
        classAmountAutoPy    = roundAuto(classAmountPy, hydrate.roundingFactorKey);
      }

      // FSA-2.2 multi-col (PPE / Intangibles / future Partnerships):
      // when the schedule's column_spec declares is_total / is_prior_year
      // columns, snapshot the live amounts into column_values[net_closing]
      // / column_values[net_py] (+ their __rounded companions) so the
      // multi-col table's Net Block display + backend AmountActual sync
      // both reflect the TB amount. No-op for 2-col schedules — the
      // column_spec parse returns no is_total flag.
      let nextColumnValues: Record<string, number> | undefined = undefined;
      if (hydrate.columnSpecJson) {
        try {
          const parsedSpec = JSON.parse(hydrate.columnSpecJson) as Array<{
            code?: string; data_type?: string;
            is_total?: boolean; is_prior_year?: boolean;
          }>;
          const totalCol = parsedSpec.find(c => c.is_total);
          const priorCol = parsedSpec.find(c => c.is_prior_year);
          if (totalCol || priorCol) {
            nextColumnValues = { ...(cd.column_values ?? {}) };
            if (totalCol?.code) {
              nextColumnValues[totalCol.code] = classAmountCy;
              nextColumnValues[totalCol.code + '__rounded'] = classAmountAutoCy;
            }
            if (priorCol?.code) {
              nextColumnValues[priorCol.code] = classAmountPy;
              nextColumnValues[priorCol.code + '__rounded'] = classAmountAutoPy;
            }
          }
        } catch { /* malformed spec — leave column_values untouched */ }
      }

      return {
        ...prev,
        class_data: {
          ...prev.class_data,
          [classCode]: {
            ...cd,
            amount_actual:               classAmountCy,
            amount_rounded_auto:         classAmountAutoCy,
            amount_rounded:              classAmountRoundedCy,
            prior_amount_actual:         classAmountPy,
            prior_amount_rounded_auto:   classAmountAutoPy,
            prior_amount_rounded:        classAmountRoundedPy,
            ledgers: liveLedgers.map((l, i) => ({
              ledger_id: l.ledgerId,
              ledger_name: l.ledgerName,
              amount_actual: l.closingActual,
              prior_amount_actual: l.openingActual,
              display_order: i + 1,
              remarks: remarksByLedgerId.get(String(l.ledgerId)) ?? null,
            })),
            line_items: refreshedLineItems,
            ...(nextColumnValues ? { column_values: nextColumnValues } : {}),
          },
        },
      };
    });
  }, [hydrate, subtotalFormulaByCode]);

  /**
   * "Recalculate Totals" — rebuild the workspace's class-level totals to
   * match what's actually visible in the Main pane. Two responsibilities,
   * one button:
   *
   *   1. <b>Drop orphan class_data entries.</b> An orphan is a key in
   *      <c>workspace.class_data</c> that doesn't match any visible class
   *      (i.e. not in <c>hydrate.liveClasses[*].classCode</c> and not in
   *      <c>workspace.added_classes[*].local_id</c>). Orphans accumulate
   *      when an earlier save left a stale custom-class entry whose
   *      <c>added_classes</c> registration was later removed or renamed —
   *      the row is gone from the UI but its <c>amount_actual</c> keeps
   *      contributing to <c>Σ class_data.amount_actual</c>, so the Note
   *      total drifts above the visible-row sum.
   *
   *   2. <b>Roll up line_items → class totals</b> for every remaining
   *      class that carries line items. Inline edits to a line item's
   *      rounded value (via <c>setLineItemRounded</c>) don't refresh the
   *      class totals on their own — this step closes that gap.
   *
   * Classes that are visible but carry NO line items are left alone — their
   * amounts come from a ledger snapshot or a manual entry, not from a
   * line-item sum, so re-deriving them would zero them out incorrectly.
   *
   * Marks the workspace dirty so Save activates whenever an orphan was
   * dropped or a class total moved.
   */
  const recalculateAllTotals = useCallback(() => {
    setWorkspace(prev => {
      const factor = hydrate?.roundingFactorKey;

      // Valid keys = template-class codes ∪ engagement-time custom-class
      // local_ids. Anything outside this set is an orphan.
      const validKeys = new Set<string>();
      for (const c of hydrate?.liveClasses ?? []) validKeys.add(c.classCode);
      for (const a of prev.added_classes ?? []) validKeys.add(a.local_id);

      const nextClassData: typeof prev.class_data = {};
      let changed = false;

      for (const [code, cd] of Object.entries(prev.class_data)) {
        if (!validKeys.has(code)) {
          // Orphan — drop it. Don't carry the entry forward.
          changed = true;
          continue;
        }

        const lis = cd.line_items ?? [];
        if (lis.length === 0) {
          // Keep visible classes with no line items as-is.
          nextClassData[code] = cd;
          continue;
        }

        // FSA-2.2 (Inventory Changes): honour template-declared subtotal
        // formula (e.g. "OPENING - CLOSING") when present; else default Σ.
        const formula = subtotalFormulaByCode.get(code);
        const cyActual  = evalClassSubtotal(lis, 'amount_actual',        formula);
        const pyActual  = evalClassSubtotal(lis, 'prior_amount_actual',  formula);
        const cyRounded = evalClassSubtotal(lis, 'amount_rounded',       formula);
        const pyRounded = evalClassSubtotal(lis, 'prior_amount_rounded', formula);
        const cyRoundedAuto = roundAuto(cyActual, factor);
        const pyRoundedAuto = roundAuto(pyActual, factor);

        if (
          cd.amount_actual !== cyActual
          || cd.prior_amount_actual !== pyActual
          || cd.amount_rounded !== cyRounded
          || cd.prior_amount_rounded !== pyRounded
          || cd.amount_rounded_auto !== cyRoundedAuto
          || cd.prior_amount_rounded_auto !== pyRoundedAuto
        ) {
          changed = true;
          nextClassData[code] = {
            ...cd,
            amount_actual: cyActual,
            prior_amount_actual: pyActual,
            amount_rounded: cyRounded,
            prior_amount_rounded: pyRounded,
            amount_rounded_auto: cyRoundedAuto,
            prior_amount_rounded_auto: pyRoundedAuto,
          };
        } else {
          nextClassData[code] = cd;
        }
      }

      // Live evaluation of is_computed classes — mirrors the backend's
      // EvaluateAndInjectComputedClassesAsync so the auditor sees fresh
      // SUM / DIVIDE results without having to save first. STATEMENT_VALUE
      // operands stay as the server-hydrated value (we don't re-fetch
      // here — that's a hydrate-time concern); SUM and DIVIDE recompute
      // from current workspace state. Order: SUM first, then DIVIDE
      // (DIVIDE may consume a SUM result, e.g. Diluted EPS = Profit /
      // SUM(WAvg Basic + Dilutive Potential)).
      const factor2 = hydrate?.roundingFactorKey;
      const computedClasses = (hydrate?.liveClasses ?? [])
        .filter(c => c.isComputed && c.computationRuleJson && c.computationRuleJson !== '{}')
        .map(c => {
          let rule: { type?: string; operands?: string[]; numerator_class?: string; denominator_class?: string } | null = null;
          try { rule = JSON.parse(c.computationRuleJson!); } catch { rule = null; }
          return { code: c.classCode, rule };
        })
        .filter(x => x.rule != null);

      const getOperand = (code: string, field: 'amount_actual' | 'prior_amount_actual' | 'amount_rounded' | 'prior_amount_rounded'): number => {
        const cd = nextClassData[code];
        if (!cd) return 0;
        return (cd as any)[field] ?? 0;
      };

      const writeComputed = (code: string, cy: number, py: number, cyR: number, pyR: number) => {
        const prev2 = nextClassData[code] ?? { line_items: [], ledgers: [] } as any;
        if (
          prev2.amount_actual === cy && prev2.prior_amount_actual === py
          && prev2.amount_rounded === cyR && prev2.prior_amount_rounded === pyR
        ) return;
        changed = true;
        const exempt = isRoundingExemptClass(code);
        nextClassData[code] = {
          ...prev2,
          amount_actual: cy,
          prior_amount_actual: py,
          amount_rounded: cyR,
          prior_amount_rounded: pyR,
          // Rounding-exempt computed classes (EPS Basic / Diluted on
          // Note 35) mirror amount_rounded; engagement factor is not
          // applied a second time.
          amount_rounded_auto: exempt ? cyR : roundAuto(cy, factor2),
          prior_amount_rounded_auto: exempt ? pyR : roundAuto(py, factor2),
        };
      };

      const safeDiv = (n: number, d: number): number => (d === 0 ? 0 : Math.round((n / d) * 100) / 100);

      // Pass A — SUM rules
      for (const { code, rule } of computedClasses) {
        if (!rule || (rule.type ?? '').toUpperCase() !== 'SUM') continue;
        const operands = rule.operands ?? [];
        if (operands.length === 0) continue;
        let cy = 0, py = 0, cyR = 0, pyR = 0;
        for (const o of operands) {
          if (!o) continue;
          cy  += getOperand(o, 'amount_actual');
          py  += getOperand(o, 'prior_amount_actual');
          cyR += getOperand(o, 'amount_rounded');
          pyR += getOperand(o, 'prior_amount_rounded');
        }
        writeComputed(code, Math.round(cy * 100) / 100, Math.round(py * 100) / 100,
                            Math.round(cyR * 100) / 100, Math.round(pyR * 100) / 100);
      }

      // Pass B — DIVIDE rules (may consume Pass A results)
      for (const { code, rule } of computedClasses) {
        if (!rule || (rule.type ?? '').toUpperCase() !== 'DIVIDE') continue;
        const n = rule.numerator_class ?? '';
        const d = rule.denominator_class ?? '';
        if (!n || !d) continue;
        const cy  = safeDiv(getOperand(n, 'amount_actual'),        getOperand(d, 'amount_actual'));
        const py  = safeDiv(getOperand(n, 'prior_amount_actual'),  getOperand(d, 'prior_amount_actual'));
        const cyR = safeDiv(getOperand(n, 'amount_rounded'),       getOperand(d, 'amount_rounded'));
        const pyR = safeDiv(getOperand(n, 'prior_amount_rounded'), getOperand(d, 'prior_amount_rounded'));
        writeComputed(code, cy, py, cyR, pyR);
      }

      if (!changed) return prev;
      return { ...prev, class_data: nextClassData };
    });
  }, [hydrate?.roundingFactorKey, hydrate?.liveClasses, subtotalFormulaByCode, isRoundingExemptClass]);

  /**
   * FSA-2.2 saved-as-primary: "Update all from Ledger" — batch the
   * single-class refresh across every class present in both the live
   * snapshot AND class_data. Done in one setWorkspace pass to avoid
   * thrashing React state with N updates.
   */
  const applyAllLedgerSnapshots = useCallback(() => {
    if (!hydrate) return;
    setWorkspace(prev => {
      const next: NoteWorkspaceData = { ...prev, class_data: { ...prev.class_data } };
      const factor = hydrate.roundingFactorKey;

      // Multi-col aware — parse the column_spec once so each refresh
      // can also snapshot live amounts into column_values[is_total /
      // is_prior_year]. No-op when not multi-col.
      let multiColTotalCode: string | null = null;
      let multiColPriorCode: string | null = null;
      if (hydrate.columnSpecJson) {
        try {
          const parsedSpec = JSON.parse(hydrate.columnSpecJson) as Array<{
            code?: string; is_total?: boolean; is_prior_year?: boolean;
          }>;
          multiColTotalCode = parsedSpec.find(c => c.is_total)?.code ?? null;
          multiColPriorCode = parsedSpec.find(c => c.is_prior_year)?.code ?? null;
        } catch { /* malformed — leave as null */ }
      }

      const refresh = (
        classCode: string,
        liveClosing: number,
        liveOpening: number,
        liveLedgers: { ledgerId: string; ledgerName: string; closingActual: number; openingActual: number }[],
      ) => {
        const cd = next.class_data[classCode];
        if (!cd) return;

        // FSA-2.2 Wave 2: preserve cross-note across refresh; long-term
        // = live − cross-note. Same rule as applyLedgerSnapshot.
        const classCrossNoteCy = cd.cross_note_amount ?? 0;
        const classCrossNotePy = cd.prior_cross_note_amount ?? 0;
        const classLongTermCy = liveClosing - classCrossNoteCy;
        const classLongTermPy = liveOpening - classCrossNotePy;
        const autoRound = roundAuto(classLongTermCy, factor);
        const autoRoundPrior = roundAuto(classLongTermPy, factor);
        const remarksByLedgerId = new Map<string, string | null | undefined>();
        for (const saved of cd.ledgers) {
          if (saved.ledger_id != null) remarksByLedgerId.set(String(saved.ledger_id), saved.remarks);
        }

        // FSA-2.2 multi-ledger line items: same per-line refresh as the
        // single-class path; preserved here so the Note-level button is
        // a true superset of the per-class action.
        // Orphan rule (same as applyLedgerSnapshot): drop references to
        // ledgers that have moved out of this class; their contribution
        // becomes zero on refresh.
        // Wave 2: each line item preserves cross-note; long-term = live − cn.
        const liveLedgerById = new Map(liveLedgers.map(l => [String(l.ledgerId), l]));
        const refreshedLineItems = cd.line_items.map(li => {
          if (li.allocation_mode !== 'ledgers') return li;
          let cySum = 0, pySum = 0;
          let displayOrder = 0;
          const nextLedgers = li.ledgers.flatMap(lref => {
            const live = lref.ledger_id != null
              ? liveLedgerById.get(String(lref.ledger_id))
              : undefined;
            if (!live) return [];
            cySum += live.closingActual;
            pySum += live.openingActual;
            return [{
              ...lref,
              ledger_name: live.ledgerName,
              amount_actual: live.closingActual,
              prior_amount_actual: live.openingActual,
              display_order: ++displayOrder,
            }];
          });
          const liCrossNoteCy = li.cross_note_amount ?? 0;
          const liCrossNotePy = li.prior_cross_note_amount ?? 0;
          const liLongTermCy = cySum - liCrossNoteCy;
          const liLongTermPy = pySum - liCrossNotePy;
          const cyAuto = roundAuto(liLongTermCy, factor);
          const pyAuto = roundAuto(liLongTermPy, factor);

          // Multi-col: mirror the refreshed CY/PY into the line item's
          // column_values[is_total / is_prior_year] + __rounded
          // companions (same fix as the single-class path above).
          let liNextColumnValues = li.column_values;
          if (multiColTotalCode || multiColPriorCode) {
            liNextColumnValues = { ...(li.column_values ?? {}) };
            if (multiColTotalCode) {
              liNextColumnValues[multiColTotalCode] = liLongTermCy;
              liNextColumnValues[multiColTotalCode + '__rounded'] = cyAuto;
            }
            if (multiColPriorCode) {
              liNextColumnValues[multiColPriorCode] = liLongTermPy;
              liNextColumnValues[multiColPriorCode + '__rounded'] = pyAuto;
            }
          }

          return {
            ...li,
            ledgers: nextLedgers,
            amount_actual: liLongTermCy,
            amount_rounded_auto: cyAuto,
            amount_rounded: cyAuto,
            prior_amount_actual: liLongTermPy,
            prior_amount_rounded_auto: pyAuto,
            prior_amount_rounded: pyAuto,
            ...(liNextColumnValues ? { column_values: liNextColumnValues } : {}),
          };
        });

        // Option A — class amount = line-item rollup when present.
        // Same rule as applyLedgerSnapshot.
        //
        // FSA-2.2 (Inventory Changes / Note 28 fix): honour the template's
        // class-level subtotal_formula (e.g. "OPENING - CLOSING") via
        // evalClassSubtotal. The Note-level "Update from Ledger" toolbar
        // button hits this code path (applyAllLedgerSnapshots) — the
        // per-class action menu hits applyLedgerSnapshot above (also
        // fixed). Both paths now match recalculateAllTotals so the
        // formula is applied consistently and the user doesn't have to
        // click Recalculate Totals as a workaround afterwards.
        let classAmountCy = classLongTermCy;
        let classAmountPy = classLongTermPy;
        let classAmountRoundedCy = autoRound;
        let classAmountRoundedPy = autoRoundPrior;
        let classAmountAutoCy = autoRound;
        let classAmountAutoPy = autoRoundPrior;
        if (refreshedLineItems.length > 0) {
          const formula = subtotalFormulaByCode.get(classCode);
          classAmountCy        = evalClassSubtotal(refreshedLineItems, 'amount_actual',        formula);
          classAmountPy        = evalClassSubtotal(refreshedLineItems, 'prior_amount_actual',  formula);
          classAmountRoundedCy = evalClassSubtotal(refreshedLineItems, 'amount_rounded',       formula);
          classAmountRoundedPy = evalClassSubtotal(refreshedLineItems, 'prior_amount_rounded', formula);
          classAmountAutoCy    = roundAuto(classAmountCy, factor);
          classAmountAutoPy    = roundAuto(classAmountPy, factor);
        }

        // Mirror the single-class snapshot's multi-col column_values
        // update so the BS / drift / display all see the same value.
        let nextColumnValues: Record<string, number> | undefined = undefined;
        if (multiColTotalCode || multiColPriorCode) {
          nextColumnValues = { ...(cd.column_values ?? {}) };
          if (multiColTotalCode) {
            nextColumnValues[multiColTotalCode] = classAmountCy;
            nextColumnValues[multiColTotalCode + '__rounded'] = classAmountAutoCy;
          }
          if (multiColPriorCode) {
            nextColumnValues[multiColPriorCode] = classAmountPy;
            nextColumnValues[multiColPriorCode + '__rounded'] = classAmountAutoPy;
          }
        }

        next.class_data[classCode] = {
          ...cd,
          amount_actual:               classAmountCy,
          amount_rounded_auto:         classAmountAutoCy,
          amount_rounded:              classAmountRoundedCy,
          prior_amount_actual:         classAmountPy,
          prior_amount_rounded_auto:   classAmountAutoPy,
          prior_amount_rounded:        classAmountRoundedPy,
          ledgers: liveLedgers.map((l, i) => ({
            ledger_id: l.ledgerId,
            ledger_name: l.ledgerName,
            amount_actual: l.closingActual,
            prior_amount_actual: l.openingActual,
            display_order: i + 1,
            remarks: remarksByLedgerId.get(String(l.ledgerId)) ?? null,
          })),
          line_items: refreshedLineItems,
          ...(nextColumnValues ? { column_values: nextColumnValues } : {}),
        };
      };

      for (const c of hydrate.liveClasses) {
        // Skip classes that aren't TB-sourced: MEMO_ONLY (Authorised
        // Capital, EPS rows), MANUAL_DISCLOSURE (ageing matrix rows),
        // and is_computed (formula-derived). Calling refresh() on these
        // would zero out the auditor's typed amounts because their
        // liveClosing/liveOpening are 0 (no TB ledgers map to them).
        const role = typeof c.classRole === 'string' ? c.classRole.toUpperCase() : '';
        if (role === 'MEMO_ONLY' || role === 'MANUAL_DISCLOSURE') continue;
        if (c.isComputed) continue;
        refresh(c.classCode, c.liveClosingActual, c.liveOpeningActual, c.ledgers as any);
      }
      for (const cc of hydrate.liveCustomClasses ?? []) {
        refresh(cc.localId, cc.liveClosingActual, cc.liveOpeningActual, cc.ledgers as any);
      }
      return next;
    });
  }, [hydrate, subtotalFormulaByCode]);

  // FSA-2.2 Wave 3 (A): refresh every cross_note-mode line item from the
  // live aggregator in one pass. Mirrors applyAllLedgerSnapshots — same
  // saved-as-primary pattern: nothing happens automatically; the auditor
  // explicitly pulls. After the pull, the workspace is dirty and the
  // auditor saves to persist the new snapshot.
  //
  // Identity per ref:
  //   xn::<sourceSchedule>::<sourceClass>::<sourceLineItem-or-_class>
  // The hydrate merge uses the same scheme so saved + live line up.
  //
  // Three kinds of change:
  //   • New ref (no saved line) — insert a fresh cross_note line item.
  //   • Same ref, different amount — update amount + recompute rounded.
  //   • Saved ref with no live counterpart — drop the line item.
  // After mutations, recompute cd.amount_actual = Σ line items (Option A).
  const applyAllCrossNoteSnapshots = useCallback(() => {
    if (!hydrate) return;
    const live = hydrate.incomingCrossNoteAmounts ?? [];
    const flows = (hydrate.inboundPlFlows ?? []).filter(f => f.distribution === 'SINGLE' && f.sourceIsSaved);
    const factor = hydrate.roundingFactorKey;
    const xnLocalId = (r: NoteIncomingCrossNote): string =>
      `xn::${r.sourceScheduleCode}::${r.sourceClassCode}::${r.sourceLineItemLocalId ?? '_class'}`;
    const plLocalId = (f: NoteInboundPlFlow): string =>
      `pl::${f.sourceReportCode}::${f.sourceLineCode}`;
    setWorkspace(prev => {
      const next: NoteWorkspaceData = { ...prev, class_data: { ...prev.class_data } };

      // Build target-class → live-refs indexes for BOTH source kinds.
      const liveXnByClass = new Map<string, NoteIncomingCrossNote[]>();
      for (const ref of live) {
        const key = ref.targetClassCode;
        if (!key) continue;
        const arr = liveXnByClass.get(key) ?? [];
        arr.push(ref);
        liveXnByClass.set(key, arr);
      }
      const livePlByClass = new Map<string, NoteInboundPlFlow[]>();
      for (const f of flows) {
        const key = f.targetClassCode;
        if (!key) continue;
        const arr = livePlByClass.get(key) ?? [];
        arr.push(f);
        livePlByClass.set(key, arr);
      }

      // Process every class touched by either source kind OR carrying
      // existing managed lines that may need to be dropped.
      const classesToTouch = new Set<string>([
        ...liveXnByClass.keys(),
        ...livePlByClass.keys(),
        ...Object.entries(next.class_data)
          .filter(([_, cd]) => (cd.line_items ?? []).some(
            li => li.allocation_mode === 'cross_note' || li.allocation_mode === 'pl_flow',
          ))
          .map(([k]) => k),
      ]);

      for (const classCode of classesToTouch) {
        let cd = next.class_data[classCode];
        const liveXnRefs = liveXnByClass.get(classCode) ?? [];
        const livePlRefs = livePlByClass.get(classCode) ?? [];
        if (!cd) {
          cd = {
            shares: null, share_type: null, face_value: null, paid_status: null,
            remarks: null, hide_in_print: false,
            amount_actual: 0, amount_rounded_auto: 0, amount_rounded: 0,
            prior_amount_actual: 0, prior_amount_rounded_auto: 0, prior_amount_rounded: 0,
            ledgers: [], line_items: [],
          };
        }
        const liveXnByLocalId = new Map(liveXnRefs.map(r => [xnLocalId(r), r]));
        const livePlByLocalId = new Map(livePlRefs.map(f => [plLocalId(f), f]));

        const nextItems: NoteLineItem[] = [];
        const handledLocalIds = new Set<string>();

        // Refresh / drop / preserve existing managed lines.
        for (const li of cd.line_items ?? []) {
          if (li.allocation_mode === 'cross_note') {
            const matched = liveXnByLocalId.get(li.local_id);
            if (!matched) continue; // orphan — drop
            handledLocalIds.add(li.local_id);
            const cyA = matched.amountCy ?? 0;
            const pyA = matched.amountPy ?? 0;
            const cyR = roundAuto(cyA, factor);
            const pyR = roundAuto(pyA, factor);
            nextItems.push({
              ...li,
              name: matched.sourceLineItemName || matched.label || li.name,
              source_ref: {
                schedule_code: matched.sourceScheduleCode,
                schedule_name: matched.sourceScheduleName,
                class_code: matched.sourceClassCode,
                line_item_local_id: matched.sourceLineItemLocalId,
                line_item_name: matched.sourceLineItemName,
              },
              amount_actual: cyA, amount_rounded_auto: cyR, amount_rounded: cyR,
              prior_amount_actual: pyA, prior_amount_rounded_auto: pyR, prior_amount_rounded: pyR,
            });
            continue;
          }
          if (li.allocation_mode === 'pl_flow') {
            const matched = livePlByLocalId.get(li.local_id);
            if (!matched) continue; // orphan — drop
            handledLocalIds.add(li.local_id);
            const cyA = matched.amountCy ?? 0;
            const pyA = matched.amountPy ?? 0;
            const cyR = roundAuto(cyA, factor);
            const pyR = roundAuto(pyA, factor);
            nextItems.push({
              ...li,
              name: matched.defaultLabel || matched.sourceLineLabel || li.name,
              source_ref: {
                schedule_code: matched.sourceReportCode,
                schedule_name: matched.sourceReportName,
                class_code: matched.sourceLineCode,
                line_item_local_id: null,
                line_item_name: matched.sourceLineLabel ?? null,
              },
              amount_actual: cyA, amount_rounded_auto: cyR, amount_rounded: cyR,
              prior_amount_actual: pyA, prior_amount_rounded_auto: pyR, prior_amount_rounded: pyR,
            });
            continue;
          }
          // Untouched: ledger / manual lines pass through.
          nextItems.push(li);
        }

        // Insert brand-new refs (no saved counterpart).
        for (const [localId, ref] of liveXnByLocalId) {
          if (handledLocalIds.has(localId)) continue;
          const cyA = ref.amountCy ?? 0;
          const pyA = ref.amountPy ?? 0;
          const cyR = roundAuto(cyA, factor);
          const pyR = roundAuto(pyA, factor);
          nextItems.push({
            local_id: localId,
            name: ref.sourceLineItemName || ref.label || 'Cross-note',
            display_order: (nextItems.length + 1) * 10,
            allocation_mode: 'cross_note',
            ledgers: [],
            manual_amount_actual: null,
            source_ref: {
              schedule_code: ref.sourceScheduleCode,
              schedule_name: ref.sourceScheduleName,
              class_code: ref.sourceClassCode,
              line_item_local_id: ref.sourceLineItemLocalId,
              line_item_name: ref.sourceLineItemName,
            },
            amount_actual: cyA, amount_rounded_auto: cyR, amount_rounded: cyR,
            prior_amount_actual: pyA, prior_amount_rounded_auto: pyR, prior_amount_rounded: pyR,
          });
        }
        for (const [localId, flow] of livePlByLocalId) {
          if (handledLocalIds.has(localId)) continue;
          const cyA = flow.amountCy ?? 0;
          const pyA = flow.amountPy ?? 0;
          const cyR = roundAuto(cyA, factor);
          const pyR = roundAuto(pyA, factor);
          nextItems.push({
            local_id: localId,
            name: flow.defaultLabel || flow.sourceLineLabel || 'Net Profit',
            display_order: (nextItems.length + 1) * 10,
            allocation_mode: 'pl_flow',
            ledgers: [],
            manual_amount_actual: null,
            source_ref: {
              schedule_code: flow.sourceReportCode,
              schedule_name: flow.sourceReportName,
              class_code: flow.sourceLineCode,
              line_item_local_id: null,
              line_item_name: flow.sourceLineLabel ?? null,
            },
            amount_actual: cyA, amount_rounded_auto: cyR, amount_rounded: cyR,
            prior_amount_actual: pyA, prior_amount_rounded_auto: pyR, prior_amount_rounded: pyR,
          });
        }

        // Option A rollup.
        const totalCy = nextItems.reduce((s, li) => s + (li.amount_actual ?? 0), 0);
        const totalPy = nextItems.reduce((s, li) => s + (li.prior_amount_actual ?? 0), 0);
        const totalCyR = nextItems.reduce((s, li) => s + (li.amount_rounded ?? 0), 0);
        const totalPyR = nextItems.reduce((s, li) => s + (li.prior_amount_rounded ?? 0), 0);
        next.class_data[classCode] = {
          ...cd,
          line_items: nextItems,
          ...(nextItems.length > 0
            ? {
                amount_actual: totalCy,
                prior_amount_actual: totalPy,
                amount_rounded: totalCyR,
                prior_amount_rounded: totalPyR,
                amount_rounded_auto: roundAuto(totalCy, factor),
                prior_amount_rounded_auto: roundAuto(totalPy, factor),
              }
            : {}),
        };
      }
      return next;
    });
  }, [hydrate]);

  // ───── Totals (used for header pills + sidebar summary) ───────────────────
  // FSA-2.4 — sums actual + rounded in one pass. Rounded sidebar values
  // come from auditor-saved `cd.amount_rounded` (their per-class rounded
  // edits + auto-rounded defaults). MEMO_ONLY / MANUAL_DISCLOSURE
  // classes excluded to match the canonical Note Total semantics.
  const totals = useMemo(() => {
    let cActual = 0, oActual = 0, cRounded = 0, oRounded = 0;
    for (const [code, cd] of Object.entries(workspace.class_data)) {
      if (memoOnlyClassCodes.has(code)) continue;
      if (manualDisclosureClassCodes.has(code)) continue;
      cActual  += cd.amount_actual;
      oActual  += cd.prior_amount_actual;
      cRounded += cd.amount_rounded;
      oRounded += cd.prior_amount_rounded;
    }
    return { cActual, oActual, cRounded, oRounded };
  }, [workspace.class_data, memoOnlyClassCodes, manualDisclosureClassCodes]);

  // FSA-2.4 N3 — Drift is now backend-computed and authoritative.
  // The hydrate carries `effectiveLedgerCy/Py` (ledger after cross-note
  // adjustments) and `noteLevelDriftCy/Py` (the auditor-facing drift).
  // Local raw-sum aggregation is gone — the backend handles cross-note
  // routing, sign-sweeps, and PL→Reserves consistently in one place.
  const reconMatches = useMemo(() => {
    if (!hydrate) return true;
    // Actual drift threshold = half-paisa (matches backend). Tolerance
    // is for rounded display only, never actual.
    return (hydrate.noteLevelDriftCy ?? 0) <= 0.005;
  }, [hydrate]);

  // Ledgers tab badge — show TOTAL ledgers mapped to this Note
  // (classified + unclassified) so the auditor sees the full workload
  // at a glance. The unclassified count surfaces as a secondary pip in
  // the sidebar; the existing schedule-only / orphan workflows stay
  // unchanged.
  const ledgersClassifiedCount = useMemo(
    () => {
      const templateLedgers = hydrate?.liveClasses.reduce((s, c) => s + c.ledgerCount, 0) ?? 0;
      // FSA-2.2 step 4b — TB ledgers mapped to custom classes count too.
      const customLedgers = hydrate?.liveCustomClasses?.reduce((s, cc) => s + cc.ledgerCount, 0) ?? 0;
      return templateLedgers + customLedgers;
    },
    [hydrate?.liveClasses, hydrate?.liveCustomClasses],
  );
  const ledgersUnclassifiedCount = hydrate?.unclassifiedLedgers?.length ?? 0;
  const ledgersTotalCount = ledgersClassifiedCount + ledgersUnclassifiedCount;

  const disclosuresCount = useMemo(() => {
    if (!hydrate?.additionalDisclosuresSpecJson) return 0;
    try {
      const parsed = JSON.parse(hydrate.additionalDisclosuresSpecJson);
      return Array.isArray(parsed) ? parsed.length : 0;
    } catch {
      return 0;
    }
  }, [hydrate?.additionalDisclosuresSpecJson]);

  // ───── Status-derived read-only ───────────────────────────────────────────
  const statusCode = hydrate?.workspace?.statusCode ?? 'DRAFT';
  const isLocked = statusCode === 'SUBMITTED' || statusCode === 'APPROVED';

  // ───── Save flow ──────────────────────────────────────────────────────────
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  // FSA-2.3 Roll Forward — per-note Import from Previous Year.
  const [importConfirmOpen, setImportConfirmOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importedPill, setImportedPill] = useState<string | null>(null);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = useCallback(() => {
    if (!scheduleId || isLocked) return;
    // FSA-2.4 N2 — auto-apply live ledger snapshots when there's
    // unsaved live drift. The amber-tinted cells already show live
    // values; clicking Save should persist exactly what the auditor
    // sees, without forcing a separate "Update from Ledger" click.
    //
    // applyAllLedgerSnapshots is idempotent (no-op when workspace
    // already matches live), so calling it unconditionally on
    // drift-present-at-hydrate is safe even if the user has since
    // clicked Update from Ledger manually. The state update completes
    // before handleConfirmSave fires (the save-confirm dialog
    // interleaves a render).
    //
    // Tradeoff: resets manually-edited rounded values to fresh
    // auto-rounded — consistent with the existing Update from Ledger
    // semantics. The user can re-tweak rounded after the save.
    // Fire on EITHER period's drift — a PY-only ledger change must also be
    // pulled in, else Save would persist the stale prior-year snapshot.
    if ((hydrate?.noteLevelDriftCy ?? 0) > 0.005 || (hydrate?.noteLevelDriftPy ?? 0) > 0.005) {
      applyAllLedgerSnapshots();
    }
    setSaveConfirmOpen(true);
  }, [scheduleId, isLocked, hydrate?.noteLevelDriftCy, hydrate?.noteLevelDriftPy, applyAllLedgerSnapshots]);

  /**
   * WYSIWYS save normalisation — re-runs the multi-column cell-edit
   * Phase B (formula + drift_formula columns) over every class row and
   * every line item. The persisted column_values then always match
   * what the UI shows on screen (the renderer evaluates the same
   * formulas at paint time).
   *
   * Triggered from BOTH the manual Save (handleConfirmSave) AND the
   * auto-save effect, so paths that don't fire Phase B at edit time
   * (TB-only classification, "open and save without editing", legacy
   * data) still produce a complete saved blob — the PDF reads
   * column_values verbatim, so missing keys would print blank otherwise.
   *
   * Returns a NEW workspace object (pure). The caller stringifies for
   * the network payload AND sets the workspace state to the result so
   * the in-memory state, the snapshot ref, and the persisted DB row
   * all agree. Without that triple-equality, isDirty would still
   * compare the un-normalised workspace against the normalised
   * snapshot and the Save button would stay active forever.
   *
   * No-op when the schedule isn't multi-column (no is_total column in
   * column_spec) or column_spec is malformed.
   */
  const normalizeWorkspaceForSave = useCallback((ws: NoteWorkspaceData): NoteWorkspaceData => {
    if (!hydrate?.columnSpecJson) return ws;

    let columns: FormatColumnSpec[];
    try {
      columns = JSON.parse(hydrate.columnSpecJson) as FormatColumnSpec[];
    } catch {
      return ws;
    }
    if (!Array.isArray(columns) || columns.length === 0) return ws;

    const hasMultiColLayout = columns.some(c => c.is_total === true);
    if (!hasMultiColLayout) return ws;

    const factor = hydrate.roundingFactorKey;
    const nextClassData: typeof ws.class_data = {};
    for (const [code, cd] of Object.entries(ws.class_data ?? {})) {
      const nextLineItems = (cd.line_items ?? []).map(li => ({
        ...li,
        column_values: recomputeFormulaColumns(li.column_values as Record<string, number> | undefined, columns, factor),
      }));
      nextClassData[code] = {
        ...cd,
        column_values: recomputeFormulaColumns(cd.column_values as Record<string, number> | undefined, columns, factor),
        line_items: nextLineItems,
      };
    }

    // Section-layout notes (Owners' Capital) derive the note-wide
    // column_totals from the per-class column_values — MainNotePane owns a
    // central effect that recomputes it (Σ across classes) on every render.
    // We just rewrote those column_values, so recompute column_totals the
    // SAME way here; otherwise the saved snapshot keeps the pre-normalise
    // totals, the post-save effect re-derives different ones, and isDirty
    // never settles → the Save button stays active forever (esp. after the
    // "Add Partners" materialise, whose rows carry no __rounded companions
    // until normalise adds them).
    let sectionLayout = false;
    try {
      const md = hydrate.scheduleMetaDataJson ? JSON.parse(hydrate.scheduleMetaDataJson) : null;
      sectionLayout = md?.display_options?.section_layout === true;
    } catch { /* malformed meta → treat as non-section */ }

    if (!sectionLayout) return { ...ws, class_data: nextClassData };

    const totals: Record<string, number> = {};
    for (const cd of Object.values(nextClassData)) {
      const cv = cd.column_values as Record<string, number> | undefined;
      if (!cv) continue;
      for (const col of columns) {
        if (col.data_type !== 'amount') continue;
        const a = cv[col.code];
        const r = cv[col.code + '__rounded'];
        if (typeof a === 'number') totals[col.code] = (totals[col.code] ?? 0) + a;
        if (typeof r === 'number') totals[col.code + '__rounded'] = (totals[col.code + '__rounded'] ?? 0) + r;
      }
    }
    return { ...ws, class_data: nextClassData, column_totals: totals };
  }, [hydrate?.columnSpecJson, hydrate?.roundingFactorKey, hydrate?.scheduleMetaDataJson]);

  const handleConfirmSave = useCallback(async () => {
    if (!scheduleId) return;
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const normalized = normalizeWorkspaceForSave(workspace);
      const blob = JSON.stringify(normalized);
      await fsaNotesService.save(engagementId, scheduleId, {
        rowVersion: hydrate?.workspace?.rowVersion ?? null,
        data: blob,
      });
      // Adopt the normalised workspace into in-memory state so isDirty
      // sees an unchanged delta against the snapshot and the Save button
      // returns to its disabled state.
      setWorkspace(normalized);
      notificationService.toast.success('Note saved.');
      initialSnapshotRef.current = blob;
      onSaved?.();
      refetch();
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      notificationService.toast.error(msg || 'Save failed.');
    } finally {
      setIsSaving(false);
    }
  }, [scheduleId, engagementId, hydrate?.workspace?.rowVersion, workspace, onSaved, refetch, normalizeWorkspaceForSave]);

  // ───── FSA-2.3 — Import from Previous Year ─────────────────────────────
  // Pulls prior engagement's saved note workspace (line item names,
  // classifications, disclosure values with per-rule transform) into this
  // engagement's note. Overwrites any saved content — the ConfirmDialog
  // gate above handles that case. Saving the note clears the "Imported"
  // pill (signals auditor review).
  const handleImportFromPriorYear = useCallback(async () => {
    if (!scheduleId) return;
    setImportConfirmOpen(false);
    setIsImporting(true);
    try {
      const response = await fsaNotesService.importFromPriorYear(engagementId, scheduleId);
      const result = response.data;
      const lines = result?.lineItemsImported ?? 0;
      const disclVerb = result?.disclosuresImportedVerbatim ?? 0;
      const disclShifted = result?.disclosuresShiftedCyToPy ?? 0;
      setImportedPill(`Imported from prior year — ${lines} line item${lines === 1 ? '' : 's'}, ${disclVerb + disclShifted} disclosure${disclVerb + disclShifted === 1 ? '' : 's'}. Review and save.`);
      notificationService.toast.success(result?.notes ?? 'Imported from prior year.');
      refetch();
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message;
      if (status === 404) {
        notificationService.toast.warning(
          msg ?? 'No prior-year data available for this note. The engagement may not be linked to a prior year, or the prior year had no saved data for this schedule.',
          6000,
        );
      } else {
        notificationService.toast.error(msg || 'Import failed.', 6000);
      }
    } finally {
      setIsImporting(false);
    }
  }, [scheduleId, engagementId, refetch]);

  // Clear the "Imported" pill once the user saves (review is implicit on save).
  useEffect(() => {
    if (!isDirty) return;
    // Pill clears as soon as user starts editing — review signal.
    if (importedPill) setImportedPill(null);
  }, [isDirty, importedPill]);

  // ───── Auto-save (Framing 1) ──────────────────────────────────────────────
  // Structural Add actions (Add Custom Class / Add Line Item / Save Line
  // Item) request an auto-save so the action's intent — "I committed this
  // change" — matches the data-layer effect. The auditor's full workspace
  // (including any in-progress amount edits) is persisted; the saved
  // values stay editable, so a typo can be fixed and re-saved at any time.
  //
  // Mechanism: a `pendingAutoSave` flag. Callers set it after mutating the
  // workspace; an effect picks the flag up AFTER React commits the workspace
  // change and runs the save with the freshest state. Direct save-after-
  // mutation would race the setWorkspace promise.
  const [pendingAutoSave, setPendingAutoSave] = useState(false);
  const requestAutoSave = useCallback(() => setPendingAutoSave(true), []);

  // Unmount signal for the auto-save effect. We can't use a `let cancelled`
  // closed over inside the effect — that flag also flips when the effect
  // re-runs due to a dep change, which is exactly what we trigger ourselves
  // when we reset `pendingAutoSave` at the start of `run()`. Cleanup fires
  // BEFORE the new effect run, sets cancelled=true, and the in-flight save
  // bails after success — so the DB persists but `setIsSaving(false)` is
  // skipped, the toast doesn't fire, refetch doesn't fire, and the UI is
  // stuck on the "Saving…" overlay until the modal is force-reopened.
  // A ref scoped to a separate "lifecycle" effect only flips on real
  // unmount, breaking the race cleanly.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Concurrency: on 409 (row_version mismatch) refetch once, re-merge with
  // the auditor's in-memory edits via the standard hydrate path, then retry
  // the save with the fresh rowVersion. Friendly toast so the auditor knows
  // a reload happened. Second 409 surfaces the error rather than looping.
  useEffect(() => {
    if (!pendingAutoSave) return;
    if (!scheduleId || isLocked) { setPendingAutoSave(false); return; }
    const run = async () => {
      setPendingAutoSave(false);
      setIsSaving(true);
      let attempt = 0;
      let currentRowVersion = hydrate?.workspace?.rowVersion ?? null;
      // Capture the workspace snapshot the effect saw on this tick; React
      // has committed the mutation by the time this effect runs. Run the
      // same WYSIWYS normalisation as handleConfirmSave so auto-saves
      // also persist formula columns (net_closing / net_py / etc.). The
      // normalised workspace is adopted on success so isDirty resets.
      const normalized = normalizeWorkspaceForSave(workspace);
      const blob = JSON.stringify(normalized);
      try {
        while (true) {
          try {
            await fsaNotesService.save(engagementId, scheduleId, {
              rowVersion: currentRowVersion,
              data: blob,
            });
            if (!isMountedRef.current) return;
            // Adopt the normalised workspace so isDirty stays clean and
            // the Save button resets to its disabled state.
            setWorkspace(normalized);
            initialSnapshotRef.current = blob;
            notificationService.toast.success('Saved.');
            onSaved?.();
            refetch();
            return;
          } catch (err) {
            const status = (err as { response?: { status?: number } })?.response?.status;
            if (status === 409 && attempt === 0) {
              // Stale rowVersion — refetch the hydrate to learn the latest,
              // then retry once. Toast to flag the silent reload.
              notificationService.toast.warning('Reloaded latest version — retrying save.');
              await refetch();
              if (!isMountedRef.current) return;
              // refetch updates `hydrate` asynchronously; read via a fresh
              // service call's response in the next tick. Simplification:
              // pass null to skip the version check on retry.
              currentRowVersion = null;
              attempt = 1;
              continue;
            }
            const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
            notificationService.toast.error(msg || 'Save failed.');
            return;
          }
        }
      } finally {
        if (isMountedRef.current) setIsSaving(false);
      }
    };
    run();
    // No cleanup needed — unmount cancellation is owned by the lifecycle
    // effect above; a dep-change cleanup here would re-introduce the race
    // that left the UI stuck on the "Saving…" overlay.
    // workspace + rowVersion must NOT be in deps — the effect should fire
    // ONLY when pendingAutoSave flips. Workspace inside the effect uses
    // the value React committed alongside the flag change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAutoSave]);

  // ───── Status transitions (FSA-2.2 stage 3) ────────────────────────────────
  const [transitionConfirm, setTransitionConfirm] = useState<{
    open: boolean;
    kind: 'submit' | 'approve' | 'reopen';
  }>({ open: false, kind: 'submit' });
  const [isTransitioning, setIsTransitioning] = useState(false);

  const openTransitionConfirm = useCallback(
    (kind: 'submit' | 'approve' | 'reopen') => {
      if (!scheduleId) return;
      if (kind === 'submit' && isDirty) {
        notificationService.toast.warning('Save your changes before submitting.');
        return;
      }
      // FSA-2.4 N3 — Submit guard: block when note-level actual drift
      // exists. The drift is authoritative (backend-computed effective
      // ledger − note total) and accounts for cross-note flows +
      // sign-sweep + PL→Reserves. Threshold = 0.005 (half paisa);
      // actuals must tally exactly. Tolerance only applies to ROUNDED
      // display, never to the actual-tally check at submit time.
      // Line-level drift is intentional and never blocks — only the
      // aggregated note-level number matters here.
      if (kind === 'submit') {
        const driftCy = hydrate?.noteLevelDriftCy ?? 0;
        if (driftCy > 0.005) {
          const fmt = driftCy.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          notificationService.toast.error(
            `Cannot submit — Note total differs from the effective ledger by ₹${fmt}. Reconcile the drift (see Deviations panel) before submitting.`,
          );
          return;
        }
      }
      setTransitionConfirm({ open: true, kind });
    },
    [scheduleId, isDirty, hydrate?.noteLevelDriftCy],
  );

  const handleSubmit  = useCallback(() => openTransitionConfirm('submit'),  [openTransitionConfirm]);
  const handleApprove = useCallback(() => openTransitionConfirm('approve'), [openTransitionConfirm]);
  const handleReopen  = useCallback(() => openTransitionConfirm('reopen'),  [openTransitionConfirm]);

  const handleConfirmTransition = useCallback(async () => {
    if (!scheduleId) return;
    const kind = transitionConfirm.kind;
    setTransitionConfirm(prev => ({ ...prev, open: false }));
    setIsTransitioning(true);
    try {
      const rowVersion = hydrate?.workspace?.rowVersion ?? null;
      const fn =
        kind === 'submit' ? fsaNotesService.submit :
        kind === 'approve' ? fsaNotesService.approve :
        fsaNotesService.reopen;
      await fn.call(fsaNotesService, engagementId, scheduleId, { rowVersion });
      const toast =
        kind === 'submit'  ? 'Note submitted for review.' :
        kind === 'approve' ? 'Note approved.' :
        'Note reopened to draft.';
      notificationService.toast.success(toast);
      onSaved?.();
      refetch();
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      notificationService.toast.error(msg || 'Status change failed.');
    } finally {
      setIsTransitioning(false);
    }
  }, [scheduleId, transitionConfirm.kind, hydrate?.workspace?.rowVersion, engagementId, onSaved, refetch]);

  // ───── Preview (server-side single-note PDF) ─────────────────────────────
  // Renders strictly from saved data (Resources/info 2.txt §1). If the note
  // has unsaved edits we ask the user to save first — the preview would
  // otherwise show stale numbers and confuse the audit trail.
  const [previewOpen, setPreviewOpen] = useState(false);
  const handlePreview = useCallback(() => {
    if (!scheduleId) return;
    if (!hydrate?.workspace) {
      notificationService.toast.warning(
        'Save the note at least once before previewing — preview renders from saved data only.',
      );
      return;
    }
    if (isDirty) {
      notificationService.toast.warning(
        'Save your changes before previewing — preview renders from saved data only.',
      );
      return;
    }
    setPreviewOpen(true);
  }, [scheduleId, hydrate?.workspace, isDirty]);

  // ───── Close flow ─────────────────────────────────────────────────────────
  const handleClose = useCallback(() => {
    if (isSaving) return;
    if (isDirty) setDiscardConfirmOpen(true);
    else onClose();
  }, [isSaving, isDirty, onClose]);

  // ───── Minimize handler ───────────────────────────────────────────────────
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !scheduleId) return;
    const savedWorkspace = workspaceRef.current;
    const savedSnapshot = initialSnapshotRef.current;
    const savedActivePane = activePaneRef.current;
    const currentIsDirty = savedSnapshot
      ? JSON.stringify(savedWorkspace) !== savedSnapshot
      : false;
    const modalId = `${tabId}-fsa-note-${scheduleId}`;
    const title = `Note — ${
      hydrate?.scheduleName ?? scheduleHeader?.name ?? scheduleHeader?.code ?? 'Edit'
    }`;
    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setWorkspace(savedWorkspace);
        initialSnapshotRef.current = savedSnapshot;
        setActivePane(savedActivePane);
        if (onRestoreModal) onRestoreModal(scheduleId);
      },
    });
    onClose();
  }, [
    tabId,
    moduleName,
    scheduleId,
    scheduleHeader,
    hydrate?.scheduleName,
    minimize,
    onClose,
    onRestoreModal,
  ]);
  if (minimizeRef) minimizeRef.current = handleMinimize;

  if (!isOpen || !scheduleId) return null;

  const factorLabel = hydrate?.roundingFactorKey ?? 'ACTUALS';
  // Use `||` (not `??`) so empty strings from the backend don't shadow the
  // scheduleHeader prop fallback (e.g. during the brief window between hydrate
  // resolving and the next render after schedule_code/name backfill).
  const headerTitle    = (hydrate?.scheduleName?.trim() || scheduleHeader?.name?.trim() || 'Note');
  const headerSubtitle = (hydrate?.scheduleCode?.trim() || scheduleHeader?.code?.trim() || '');

  return (
    <>
      <div className="modal-backdrop" onClick={isSaving ? undefined : handleClose} />
      <div
        className="modal-container modal-xl"
        style={{ display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}
      >
        {isSaving && <LoadingOverlay />}

        {/* ───── Header ─────────────────────────────────────────────────── */}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
            <h2 className="modal-title" style={{ marginBottom: 0 }}>
              {headerTitle}{' '}
              <span style={subTitleStyle}>· {headerSubtitle}</span>
            </h2>
            {/* FSA-2.4 N1 — header chips slimmed.
                Removed: ReconPill (recon state now lives in the sidebar
                Drift group + the Deviations panel — single source of
                truth instead of duplicating across header + toolbar +
                sidebar). Rounding moved to a hover tooltip on the
                status pill so static settings don't compete with
                dynamic state. Last-saved compressed to date+time only
                (the "by name" tail was rarely informative for a
                single-auditor workflow and ate horizontal space).
                Drop "Recon" / "Rounding" eyes; keep "Status" + "Last
                saved" only — both genuinely dynamic. */}
            {/* FSA-2.4 — Display toggle (Actual / Rounded) lives in the
                header so it applies note-wide (Main Note + Additional
                Disclosures share the same display setting). Factor
                (THOUSANDS / LAKHS / CRORES / ACTUALS) is informational
                — the auditor sets it once on the engagement; the
                tooltip on the Rounded button carries it so the toggle
                stays clean even when factor names are long. */}
            <div style={headerMetaRowStyle}>
              <StatusPill status={statusCode} />
              <div style={headerToggleGroupStyle}>
                <button
                  type="button"
                  onClick={() => setDisplayMode('actual')}
                  style={headerToggleBtnStyle(displayMode === 'actual')}
                  title="Show actual values (full precision)"
                >
                  Actual
                </button>
                <button
                  type="button"
                  onClick={() => setDisplayMode('rounded')}
                  style={headerToggleBtnStyle(displayMode === 'rounded')}
                  title={`Show rounded values (engagement factor: ${factorLabel})`}
                >
                  Rounded
                </button>
              </div>
              {hydrate?.workspace?.updatedAt && (
                <span
                  style={metaTextStyle}
                  title={hydrate.workspace.updatedByName
                    ? `Last saved ${new Date(hydrate.workspace.updatedAt).toLocaleString()} by ${hydrate.workspace.updatedByName}`
                    : undefined}
                >
                  Last saved {new Date(hydrate.workspace.updatedAt).toLocaleString()}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button
                className="modal-header-btn"
                onClick={handleMinimize}
                title="Minimize to bar"
                disabled={isSaving}
              >
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button
              className="modal-close"
              onClick={handleClose}
              disabled={isSaving}
              title="Close"
            >
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        {/* ───── Body: sidebar + active pane ───────────────────────────── */}
        <div style={bodyStyle}>
          {hydrating || !hydrate ? (
            <div style={loadingPlaceholderStyle}>
              {hydrating ? 'Loading…' : 'Note not found.'}
            </div>
          ) : (
            <>
              <NoteSidebar
                activePane={activePane}
                onSelect={setActivePane}
                ledgersCount={ledgersTotalCount}
                ledgersPending={ledgersUnclassifiedCount}
                disclosuresCount={disclosuresCount}
                summary={(() => {
                  // FSA-2.4 — sidebar receives both actual + rounded
                  // for Effective Ledger / Note Amount / Drift so it
                  // can mirror the header Actual/Rounded toggle.
                  // Effective Ledger rounded is auto-rounded from the
                  // backend's authoritative actual (no separate stored
                  // rounded — it's a derived figure, not auditor-
                  // entered). Note Amount rounded uses the auditor's
                  // saved per-class rounded sums. Drift is computed
                  // for each mode against its own pair.
                  const factor = hydrate?.roundingFactorKey;
                  const effCyA = hydrate?.effectiveLedgerCy ?? 0;
                  const effPyA = hydrate?.effectiveLedgerPy ?? 0;
                  const effCyR = roundAuto(effCyA, factor);
                  const effPyR = roundAuto(effPyA, factor);
                  return {
                    cActual: totals.cActual,
                    oActual: totals.oActual,
                    cRounded: totals.cRounded,
                    oRounded: totals.oRounded,
                    effectiveLedgerCyActual: effCyA,
                    effectiveLedgerPyActual: effPyA,
                    effectiveLedgerCyRounded: effCyR,
                    effectiveLedgerPyRounded: effPyR,
                    driftCyActual: hydrate?.noteLevelDriftCy ?? 0,
                    driftPyActual: hydrate?.noteLevelDriftPy ?? 0,
                    driftCyRounded: Math.abs(totals.cRounded - effCyR),
                    driftPyRounded: Math.abs(totals.oRounded - effPyR),
                  };
                })()}
                displayMode={displayMode}
                roundingTolerance={hydrate?.roundingTolerance ?? 1.0}
              />
              <div style={paneContainerStyle}>
                {/* All panes stay mounted; only the active one is shown.
                    Switching the left menu preserves each pane's local
                    state — the inline line-item editor's draft form,
                    DisclosuresPane's expanded sections, LedgersPane's
                    search input — all survive across switches. Shared
                    edits (workspace.class_data, additional_disclosures,
                    etc.) already persist via setState; this fix is
                    specifically for the component-local state that gets
                    discarded on unmount. */}
                <div style={{ display: activePane === 'overview' ? 'block' : 'none', height: '100%' }}>
                  <OverviewPane hydrate={hydrate} workspace={workspace} isDirty={isDirty} setAppropriationRouting={setAppropriationRouting} />
                </div>
                <div style={{ display: activePane === 'ledgers' ? 'block' : 'none', height: '100%' }}>
                  <LedgersPane
                    hydrate={hydrate}
                    readOnly={isLocked}
                    engagementId={engagementId}
                    addedClasses={workspace.added_classes}
                    onRefetch={refetch}
                    tabId={tabId}
                    moduleName={moduleName}
                  />
                </div>
                <div style={{ display: activePane === 'main-note' ? 'block' : 'none', height: '100%' }}>
                  {hydrate.formatType === 'POLICY' ? (
                    // POLICY-format notes (Accounting Policies / Corporate
                    // Information) — replace the class-table view with the
                    // section-list + TipTap editor pane. Edits only update
                    // the in-memory workspace state (which makes isDirty
                    // turn true, lighting the Save button); we deliberately
                    // do NOT call requestAutoSave() — long rich-text edits
                    // shouldn't hammer the DB on every keystroke. Persistence
                    // happens only when the auditor clicks Save.
                    <PolicyEditorPane
                      hydrate={hydrate}
                      workspace={workspace}
                      setWorkspace={setWorkspace}
                      readOnly={isLocked}
                    />
                  ) : (
                  <MainNotePane
                    hydrate={hydrate}
                    engagementId={engagementId}
                    workspace={workspace}
                    setClassField={setClassField}
                    setClassActual={setClassActual}
                    setLineItems={setLineItems}
                    setLineItemRounded={setLineItemRounded}
                    resetRoundingFromActuals={resetRoundingFromActuals}
                    setNoteRemark={setNoteRemark}
                    setClassHideInPrint={setClassHideInPrint}
                    autoHideEmptyClasses={autoHideEmptyClasses}
                    addCustomClass={addCustomClass}
                    renameCustomClass={renameCustomClass}
                    deleteCustomClass={deleteCustomClass}
                    setClassLabelOverride={setClassLabelOverride}
                    applyLedgerSnapshot={applyLedgerSnapshot}
                    applyAllLedgerSnapshots={applyAllLedgerSnapshots}
                    recalculateAllTotals={recalculateAllTotals}
                    requestAutoSave={requestAutoSave}
                    applyAllCrossNoteSnapshots={applyAllCrossNoteSnapshots}
                    setClassDimensionValue={setClassDimensionValue}
                    setPriorYearTotal={setPriorYearTotal}
                    setColumnTotals={setColumnTotals}
                    readOnly={isLocked}
                    displayMode={displayMode}
                    setDisplayMode={setDisplayMode}
                    refetchHydrate={refetch}
                  />
                  )}
                </div>
                <div style={{ display: activePane === 'disclosures' ? 'block' : 'none', height: '100%' }}>
                  <DisclosuresPane
                    additionalDisclosuresSpecJson={hydrate.additionalDisclosuresSpecJson}
                    workspace={workspace}
                    setDisclosure={setDisclosure}
                    toggleDisclosureHidden={toggleDisclosureHidden}
                    autoHideEmptyDisclosures={autoHideEmptyDisclosures}
                    readOnly={isLocked}
                    roundingFactorKey={hydrate.roundingFactorKey}
                    displayMode={displayMode}
                    setDisplayMode={setDisplayMode}
                    valueSourceLiveValues={hydrate.valueSourceLiveValues}
                  />
                </div>
                <div style={{ display: activePane === 'auditor-notes' ? 'block' : 'none', height: '100%' }}>
                  <AuditorNotesPane
                    notesText={workspace.notes_text}
                    setNotesText={setNotesText}
                    readOnly={isLocked}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        {/* ───── Footer ────────────────────────────────────────────────── */}
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          {statusCode === 'DRAFT' && (
            <>
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={isSaving || isTransitioning || !canSave}
                title={
                  !canSave
                    ? 'No changes to save'
                    : !isDirty && !hydrate?.workspace
                    ? 'Save the initial snapshot for this Note'
                    : !isDirty
                    ? 'Cross-note totals changed since last save — re-save to reconcile'
                    : undefined
                }
              >
                {isSaving ? 'Saving…' : 'Save'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  // ConfirmDialog only when the note already has saved content.
                  if (hydrate?.workspace) setImportConfirmOpen(true);
                  else void handleImportFromPriorYear();
                }}
                disabled={isSaving || isTransitioning || isImporting || !hydrate?.hasPriorEngagement}
                title={
                  !hydrate?.hasPriorEngagement
                    ? 'This engagement has no linked prior-year engagement (first-year or stand-alone). Link a prior engagement in FSA Settings to enable Import.'
                    : "Import line items, classifications, and disclosures from the prior year's saved note"
                }
              >
                <IconSystem.download size={14} /> {isImporting ? 'Importing…' : 'Import from Previous Year'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleSubmit}
                disabled={isSaving || isTransitioning || isDirty || !hydrate?.workspace}
                title={
                  isDirty
                    ? 'Save before submitting'
                    : !hydrate?.workspace
                    ? 'Save at least once before submitting'
                    : undefined
                }
              >
                Submit
              </button>
            </>
          )}
          {statusCode === 'SUBMITTED' && (
            <>
              <button
                className="btn btn-primary"
                onClick={handleApprove}
                disabled={isTransitioning}
              >
                Approve
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleReopen}
                disabled={isTransitioning}
              >
                Reopen
              </button>
            </>
          )}
          {statusCode === 'APPROVED' && (
            <button
              className="btn btn-secondary"
              onClick={handleReopen}
              disabled={isTransitioning}
            >
              Reopen
            </button>
          )}
          <button
            className="btn btn-secondary"
            onClick={handlePreview}
            disabled={isSaving || isTransitioning}
            title="Render this note as it appears in the printed FS"
          >
            <IconSystem.eye size={14} /> Preview
          </button>
          <button
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={isSaving || isTransitioning}
          >
            Close
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Save Note"
        message="Save the current note data? Actual amounts will be refreshed from the latest TB on the server before persisting."
        confirmLabel="Save"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />
      <ConfirmDialog
        open={importConfirmOpen}
        title="Import from Previous Year?"
        message="This note has saved data. Importing from the previous year will replace the line items, classifications, and disclosure values with the prior year's structure. Amounts will be reset to 0. Continue?"
        confirmLabel="Import"
        cancelLabel="Cancel"
        variant="danger"
        loading={isImporting}
        loadingMessage="Importing…"
        onConfirm={handleImportFromPriorYear}
        onCancel={() => setImportConfirmOpen(false)}
      />
      <ConfirmDialog
        open={transitionConfirm.open}
        title={
          transitionConfirm.kind === 'submit'  ? 'Submit Note for Review' :
          transitionConfirm.kind === 'approve' ? 'Approve Note' :
          'Reopen Note for Editing'
        }
        message={
          transitionConfirm.kind === 'submit'
            ? 'Submit this note for partner review? It will be locked from edits until approved or reopened.'
            : transitionConfirm.kind === 'approve'
            ? 'Approve this note. Once approved, it stays locked from edits unless reopened.'
            : 'Reopen this note to make further edits? Status returns to DRAFT.'
        }
        confirmLabel={
          transitionConfirm.kind === 'submit'  ? 'Submit'  :
          transitionConfirm.kind === 'approve' ? 'Approve' :
          'Reopen'
        }
        cancelLabel="Cancel"
        variant={transitionConfirm.kind === 'reopen' ? 'danger' : 'default'}
        loading={isTransitioning}
        loadingMessage={
          transitionConfirm.kind === 'submit'  ? 'Submitting…'  :
          transitionConfirm.kind === 'approve' ? 'Approving…'   :
          'Reopening…'
        }
        onConfirm={handleConfirmTransition}
        onCancel={() => setTransitionConfirm(prev => ({ ...prev, open: false }))}
      />
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Discard them and close the note?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => {
          setDiscardConfirmOpen(false);
          onClose();
        }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />

      <FsaNotePreviewModal
        isOpen={previewOpen}
        engagementId={previewOpen ? engagementId : null}
        scheduleId={previewOpen ? scheduleId : null}
        scheduleCode={hydrate?.scheduleCode ?? scheduleHeader?.code ?? null}
        scheduleName={hydrate?.scheduleName ?? scheduleHeader?.name ?? null}
        onClose={() => setPreviewOpen(false)}
      />
    </>
  );
};

// ───── status / recon pills ──────────────────────────────────────────────────

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
  const palette: Record<string, { bg: string; fg: string }> = {
    DRAFT:     { bg: '#e5e7eb', fg: '#374151' },
    SUBMITTED: { bg: '#fef3c7', fg: '#92400e' },
    APPROVED:  { bg: '#dcfce7', fg: '#166534' },
  };
  const p = palette[status] ?? palette.DRAFT;
  return (
    <span
      style={{
        background: p.bg,
        color: p.fg,
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: 0.4,
      }}
    >
      ● {status}
    </span>
  );
};

// FSA-2.4 N1 — ReconPill removed. Recon state surfaces in the sidebar
// Drift group + the Deviations panel; header doesn't need its own copy.

// ───── loading overlay ───────────────────────────────────────────────────────

const LoadingOverlay: React.FC = () => (
  <div
    style={{
      position: 'absolute',
      inset: 0,
      background: 'var(--modal-loading-overlay)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10,
      borderRadius: 'inherit',
      gap: '0.75rem',
    }}
  >
    <div
      style={{
        width: '2rem',
        height: '2rem',
        border: '3px solid var(--border-color)',
        borderTopColor: '#2563eb',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }}
    />
    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
      Saving…
    </span>
  </div>
);

// ───── helpers ───────────────────────────────────────────────────────────────

const roundAuto = (actual: number, factor: string | null | undefined): number => {
  const divisor =
    factor === 'THOUSANDS' ? 1000 :
    factor === 'LAKHS' ? 100000 :
    factor === 'CRORES' ? 10000000 :
    1;
  return Math.round((actual / divisor) * 100) / 100;
};

// ───── FSA ICAI NCE — appropriation routing (Capital vs Current Account) ──────
// Each appropriation (remuneration, interest on capital, share of profit/loss)
// posts to the Capital or Current account per an engagement-editable routing
// map. The two helpers below are SHARED by the hydrate merge and the live
// re-route handler so both produce identical placement.

interface ApprRoutingCtx {
  pcCols: FormatColumnSpec[];
  factor?: string | null;
  partnerLinesClasses: string[];
  /** partner local_id → derived column values (remuneration, interest). */
  derived: Record<string, Record<string, number>>;
  /** P&L profit routed into the note (for share_of_profit_loss = profit × PSR). */
  inboundProfit: number;
  /** partner local_id → profit-sharing ratio (%). */
  psrByLocalId: Record<string, number>;
  /** appropriation column codes (keys of the routing map). */
  appropriationCols: string[];
  /** the is_total (closing) column code. */
  totalColCode: string;
  /** template default routing (col → section). */
  defaultRouting: Record<string, string>;
}

/** Builds the routing context from a hydrate. Returns null when the note is not
 *  an appropriation-routing note (no `appropriation_routing` in meta, or not a
 *  multi-column note — i.e. it isn't the Owners' Capital note). */
function buildApprCtx(hydrate: NoteHydrate): ApprRoutingCtx | null {
  let defaultRouting: Record<string, string> = {};
  try {
    const md = JSON.parse(hydrate.scheduleMetaDataJson ?? '{}') as { appropriation_routing?: Record<string, string> };
    defaultRouting = md?.appropriation_routing ?? {};
  } catch { defaultRouting = {}; }
  const appropriationCols = Object.keys(defaultRouting);
  if (appropriationCols.length === 0) return null;

  let pcCols: FormatColumnSpec[] = [];
  try { pcCols = JSON.parse(hydrate.columnSpecJson ?? '[]') as FormatColumnSpec[]; } catch { pcCols = []; }
  const totalCol = pcCols.find(c => c.is_total);
  if (!totalCol) return null;

  const partnerLinesClasses = (hydrate.liveClasses ?? [])
    .filter(c => { try { return (JSON.parse(c.metaDataJson ?? '{}') as { partner_lines?: boolean })?.partner_lines === true; } catch { return false; } })
    .map(c => c.classCode);
  const inboundProfit = (hydrate.inboundPlFlows ?? []).find(
    f => f.targetClassCode === 'PC_CURRENT_ACCOUNT',
  )?.amountCy ?? 0;
  const psrByLocalId: Record<string, number> = {};
  for (const p of (hydrate.partners ?? [])) psrByLocalId[`partner::${p.id}`] = p.profitSharingRatio ?? 0;

  return {
    pcCols, factor: hydrate.roundingFactorKey, partnerLinesClasses,
    derived: hydrate.partnerDerivedColumns ?? {}, inboundProfit, psrByLocalId,
    appropriationCols, totalColCode: totalCol.code, defaultRouting,
  };
}

/** Places each appropriation's per-partner value into its routed section (0 in
 *  the others), recomputes each partner row's closing, and re-rolls class
 *  column_values + legacy amount fields + note-wide column_totals. Pure —
 *  returns new class_data + column_totals. Non-appropriation columns (opening,
 *  capital introduced, withdrawals, share %) are preserved untouched. */
function applyAppropriationRouting(
  classData: NoteWorkspaceData['class_data'],
  routing: Record<string, string>,
  ctx: ApprRoutingCtx,
): { classData: NoteWorkspaceData['class_data']; columnTotals: Record<string, number> } {
  const { pcCols, factor, partnerLinesClasses, derived, inboundProfit, psrByLocalId, appropriationCols, totalColCode } = ctx;
  const round2 = (n: number) => Math.round(n * 100) / 100;
  const recomputeClosing = (cv: Record<string, number>): number => {
    let sum = 0;
    for (const col of pcCols) {
      if (col.data_type !== 'amount' || col.is_total) continue;
      sum += (col.is_deduction ? -1 : 1) * (cv[col.code] ?? 0);
    }
    return round2(sum);
  };

  const next: NoteWorkspaceData['class_data'] = { ...classData };
  for (const classCode of partnerLinesClasses) {
    const cd = next[classCode];
    if (!cd) continue;
    const lineItems = (cd.line_items ?? []).map(li => {
      if (!li.local_id?.startsWith('partner::')) return li;
      const cv: Record<string, number> = { ...(li.column_values ?? {}) };
      for (const col of appropriationCols) {
        const routedHere = (routing[col] ?? '') === classCode;
        let val = 0;
        if (routedHere) {
          val = col === 'share_of_profit_loss'
            ? round2(inboundProfit * (psrByLocalId[li.local_id] ?? 0) / 100)
            : (derived[li.local_id]?.[col] ?? 0);
        }
        cv[col] = val;
        cv[col + '__rounded'] = roundAuto(val, factor);
      }
      const closing = recomputeClosing(cv);
      cv[totalColCode] = closing;
      cv[totalColCode + '__rounded'] = roundAuto(closing, factor);
      return {
        ...li, column_values: cv,
        amount_actual: closing,
        amount_rounded: roundAuto(closing, factor),
        amount_rounded_auto: roundAuto(closing, factor),
      };
    });
    const classCv: Record<string, number> = {};
    for (const li of lineItems) {
      const lcv = li.column_values ?? {};
      for (const k of Object.keys(lcv)) classCv[k] = (classCv[k] ?? 0) + (lcv[k] ?? 0);
    }
    const cyA = lineItems.reduce((s, li) => s + (li.amount_actual ?? 0), 0);
    const pyA = lineItems.reduce((s, li) => s + (li.prior_amount_actual ?? 0), 0);
    next[classCode] = {
      ...cd, line_items: lineItems, column_values: classCv,
      amount_actual: cyA, prior_amount_actual: pyA,
      amount_rounded: roundAuto(cyA, factor), prior_amount_rounded: roundAuto(pyA, factor),
      amount_rounded_auto: roundAuto(cyA, factor), prior_amount_rounded_auto: roundAuto(pyA, factor),
    };
  }

  const amountCols = pcCols.filter(c => c.data_type === 'amount');
  const columnTotals: Record<string, number> = {};
  for (const code of Object.keys(next)) {
    const ccv = next[code]?.column_values;
    if (!ccv) continue;
    for (const col of amountCols) {
      const a = ccv[col.code];
      const r = ccv[col.code + '__rounded'];
      if (typeof a === 'number') columnTotals[col.code] = (columnTotals[col.code] ?? 0) + a;
      if (typeof r === 'number') columnTotals[col.code + '__rounded'] = (columnTotals[col.code + '__rounded'] ?? 0) + r;
    }
  }
  return { classData: next, columnTotals };
}

// ───── styles ────────────────────────────────────────────────────────────────

const subTitleStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 500,
  color: 'var(--text-secondary)',
};
const headerMetaRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginTop: 4,
  flexWrap: 'wrap',
};
const metaPillStyle: React.CSSProperties = {
  background: '#dbeafe',
  color: '#1e40af',
  padding: '2px 8px',
  borderRadius: 999,
  fontSize: '0.7rem',
  fontWeight: 600,
};
const metaTextStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  color: 'var(--text-secondary)',
};

// FSA-2.4 — header Actual/Rounded toggle. Compact two-button group
// styled to fit alongside the StatusPill. Same visual shape as the
// existing toolbar toggle (now removed from MainNotePane) — auditors
// already recognise this pattern.
const headerToggleGroupStyle: React.CSSProperties = {
  display: 'inline-flex',
  border: '1px solid var(--border-color)',
  borderRadius: 999,
  overflow: 'hidden',
  background: 'var(--bg-secondary)',
};
const headerToggleBtnStyle = (active: boolean): React.CSSProperties => ({
  padding: '2px 10px',
  border: 'none',
  background: active ? '#2563eb' : 'transparent',
  color: active ? 'white' : 'var(--text-secondary)',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '0.7rem',
  fontWeight: 600,
});

const bodyStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  overflow: 'hidden',
  minHeight: 400,
};
const paneContainerStyle: React.CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
};
const loadingPlaceholderStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--text-secondary)',
  padding: '2rem',
};

export default FsaNoteModal;
